import {
  ChangeDetectionStrategy,
  Component,
  inject,
  effect,
  ElementRef,
  viewChild,
  OnInit,
  OnDestroy,
  signal,
  computed,
  PLATFORM_ID,
} from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { RouterModule, Router, ActivatedRoute } from "@angular/router";
import { DomSanitizer } from "@angular/platform-browser";
import { animate, stagger } from "motion";
import { AuthService, MASTER_ADMIN_EMAIL } from "./auth.service";
import { ThemeService } from "./theme";
import { NotificationService } from "./notification";
import { SearchService } from "./search.service";
import { SearchDataService } from "./services/search-data.service";
import { ChatService } from "./services/chat.service";
import { CountFormatPipe } from "./count-format.pipe";
import { PersonCard } from "./person-card";
import { OrganizationCard } from "./organization-card";
import { CountryCard } from "./country-card";
import { WikipediaSearchComponent } from "./wikipedia-search.component";
import { Animated3dIconComponent } from "./animated-3d-icon.component";
import { UserImage } from "./models/image.model";
import { AuthProfile } from "./models/auth.model";
import { JobModel } from "./models/job.model";
import { ProductModel } from "./models/product.model";
import { VideoModel } from "./models/video.model";
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, onSnapshot, deleteDoc, Unsubscribe, setDoc, getDocs, query, where, serverTimestamp, startAfter, limit, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { ContactItem } from "./contacts";

export interface Shortcut {
  label: string;
  icon: string;
  path: string;
  queryParams?: Record<string, string | undefined>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-search",
  host: {
    '(touchstart)': 'onGlobalTouchStart($event)',
    '(touchend)': 'onGlobalTouchEnd($event)',
  },
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    RouterModule,
    CountFormatPipe,
    PersonCard,
    OrganizationCard,
    CountryCard,
    WikipediaSearchComponent,
    Animated3dIconComponent,
  ],
  styles: [
    `
      @reference "../styles.css";

      .touch-optimized-card {
        touch-action: manipulation;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }

      @media (hover: hover) and (pointer: fine) {
        .touch-optimized-card:hover {
          box-shadow: var(--shadow-ios-md);
        }
        .touch-optimized-card:hover img {
          transform: scale(1.05);
        }
        .touch-optimized-card:hover .card-overlay {
          opacity: 1;
        }
      }
    `,
  ],
  template: `
    @if (!isMounted()) {
      <div
        style="display: flex; min-height: 100vh; width: 100%; align-items: center; justify-content: center; background: transparent;"
      ></div>
    } @else {
      <div
        class="min-h-screen w-full flex flex-col transition-colors duration-150 relative overflow-x-hidden motion-reduce:transition-none selection:bg-ios-blue/20"
        [class.justify-center]="!hasSearched()"
        style="touch-action: pan-y; -webkit-overflow-scrolling: touch;"
      >
        <!-- Header (Search Result State) -->
        @if (hasSearched()) {
          <header class="ios-header-liquid !h-auto py-2 md:py-3">
            <div class="max-w-[1000px] mx-auto w-full">
              <div
                class="glass-card !rounded-full mb-4 px-3 md:px-5 py-2 md:py-2.5 flex items-center justify-between gap-2 md:gap-4 border border-gray-300 dark:border-gray-700 shadow-sm backdrop-blur-md"
                [class.h-14]="hasSearched()"
                [class.scale-[0.98]]="isScrolled()"
              >
                <!-- Logo -->
                <button
                  type="button"
                  class="flex items-center cursor-pointer active:scale-95 transition-all duration-300 group border-none bg-transparent p-0 shrink-0 pl-1 pr-3 md:pl-2 md:pr-4"
                  (click)="onReset()"
                  (keydown.enter)="onReset()"
                  title="Kicgle Home"
                >
                  <div
                    class="relative w-11 h-11 md:w-13 md:h-13 flex items-center justify-center"
                  >
                    <div
                      class="absolute inset-0 bg-ios-blue/30 blur-xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    ></div>
                    <img
                      src="kicgle.svg"
                      alt="Kicgle"
                      class="w-full h-full object-contain relative z-10 drop-shadow-sm scale-110 overflow-visible"
                      referrerpolicy="no-referrer"
                    />
                  </div>
                </button>

                <!-- Search Input -->
                <form
                  (ngSubmit)="onSearch()"
                  class="flex-1 flex items-center gap-2 md:gap-3"
                >
                  <div
                    class="w-px h-6 md:h-7 bg-light-border dark:bg-dark-border opacity-60 shrink-0"
                  ></div>
                  <div class="relative flex-1 flex items-center pl-1 md:pl-2">
                    <input
                      type="text"
                      [ngModel]="query()"
                      (ngModelChange)="query.set($event)"
                      name="search"
                      placeholder="Search anything..."
                      class="w-full bg-transparent border-none outline-none text-[15px] md:text-[17px] font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 px-2 pr-20 md:pr-24"
                      autocomplete="off"
                    />
                    <div class="absolute inset-y-0 right-0 flex items-center gap-1 md:gap-2 pr-2">
                      @if (query()) {
                        <button
                          type="button"
                          (click)="query.set('')"
                          class="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-ios-blue/50 transition-all duration-300"
                          title="Clear search"
                        >
                          <mat-icon class="!text-lg">close</mat-icon>
                        </button>
                      }
                      <button
                        type="button"
                        (click)="onVoiceSearch()"
                        class="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-ios-blue/10 hover:bg-ios-blue/20 text-ios-blue shadow-inner transition-all duration-300 active:scale-90"
                        [class.animate-pulse]="isListening()"
                        [class.!bg-ios-red]="isListening()"
                        [class.!text-white]="isListening()"
                        aria-label="Voice Search"
                      >
                        <app-animated-3d-icon icon="mic" variant="3d" class="w-5 h-5 md:w-6 md:h-6"></app-animated-3d-icon>
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <!-- Category Navigation (Results Categories) -->
              <div
                class="chip-scroll-container animate-ios-fade touch-pan-x"
                style="animation-delay: 0.2s; touch-action: pan-x; -webkit-overflow-scrolling: touch;"
                #headerChipScrollContainer
                (mousedown)="startChipDrag($event, headerChipScrollContainer)"
                (mouseleave)="stopChipDrag()"
                (mouseup)="stopChipDrag()"
                (mousemove)="onChipDrag($event, headerChipScrollContainer)"
              >
                @for (cat of searchCategories; track cat.name) {
                  <button
                    [id]="'cat-btn-' + cat.name.toLowerCase()"
                    (click)="onCategorySelect(cat.name)"
                    class="ios-liquid-chip relative"
                    [class.active]="category() === cat.name.toLowerCase()"
                  >
                    <app-animated-3d-icon [icon]="cat.icon" variant="3d" class="w-4 h-4 md:w-5 md:h-5"></app-animated-3d-icon>
                    <span>{{ cat.name }}</span>
                  </button>
                }
              </div>
            </div>
          </header>
        }

        <!-- Main Content -->
        <main
          class="flex-1 flex flex-col items-center px-4 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none relative z-10 w-full"
          [class.justify-center]="!hasSearched()"
          [class.pt-56]="hasSearched()"
          [class.md:pt-52]="hasSearched()"
          style="touch-action: pan-y; -webkit-overflow-scrolling: touch;"
        >
          <!-- Hero (Initial View) -->
          @if (!hasSearched()) {
            <div
              class="w-full max-w-[750px] flex flex-col items-center animate-ios-fade"
            >
              <!-- Logo -->
              <button
                type="button"
                class="mt-[50px] mb-[50px] relative group cursor-pointer active:scale-95 transition-transform duration-200 border-none bg-transparent p-0 block mx-auto outline-none motion-reduce:transition-none"
                (click)="onReset()"
                (keydown.enter)="onReset()"
              >
                <div
                  class="absolute inset-0 bg-ios-blue/30 blur-[120px] rounded-full scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-300 motion-reduce:transition-none"
                ></div>
                <img
                  src="kicgle.svg"
                  alt="Kicgle"
                  class="w-[80%] max-w-[300px] md:max-w-[400px] lg:max-w-[500px] h-auto relative z-10 drop-shadow-[0_25px_60px_rgba(0,122,255,0.4)] object-contain block mx-auto overflow-visible"
                  referrerpolicy="no-referrer"
                />
              </button>

              <!-- Search Bar -->
              <div
                class="w-full glass-card !rounded-full p-1.5 md:p-2 flex items-center gap-3 md:gap-4 border border-gray-300 dark:border-gray-700 shadow-sm focus-within:shadow-md focus-within:-translate-y-1 transition-all duration-300 backdrop-blur-md"
              >
                <div
                  class="w-10 h-10 md:w-12 md:h-12 rounded-full bg-ios-blue/10 flex items-center justify-center text-ios-blue shadow-inner shrink-0 ml-1 pl-1"
                >
                  <app-animated-3d-icon icon="search" variant="3d" class="w-6 h-6 md:w-7 md:h-7"></app-animated-3d-icon>
                </div>
                <form (ngSubmit)="onSearch()" class="flex-1 flex items-center">
                  <div class="relative flex-1 flex items-center">
                    <input
                      type="text"
                      [ngModel]="query()"
                      (ngModelChange)="query.set($event)"
                      name="search"
                      placeholder="What's on your mind?"
                      class="w-full bg-transparent border-none outline-none text-[16px] md:text-[20px] font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 px-2 pr-24 md:pr-28"
                      autocomplete="off"
                    />
                    <div class="absolute inset-y-0 right-0 flex items-center gap-2 md:gap-3 pr-2 md:pr-3">
                      @if (query()) {
                        <button
                          type="button"
                          (click)="query.set('')"
                          class="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-ios-blue/40 transition-all duration-300"
                          title="Clear search"
                        >
                          <mat-icon class="!text-lg md:!text-xl">close</mat-icon>
                        </button>
                      }
                      <button
                        type="button"
                        (click)="onVoiceSearch()"
                        class="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-ios-blue/10 hover:bg-ios-blue/20 text-ios-blue shadow-inner hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 shrink-0"
                        [class.animate-pulse]="isListening()"
                        [class.!bg-ios-red]="isListening()"
                        [class.!text-white]="isListening()"
                        aria-label="Voice Search"
                      >
                        <app-animated-3d-icon icon="mic" variant="3d" class="w-6 h-6 md:w-7 md:h-7"></app-animated-3d-icon>
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <!-- Horizontal Row of Swipeable Category Chips ('All', 'Search', 'Productivity', 'System') -->
              <div
                class="w-full mt-4 md:mt-5 relative z-10 flex flex-col items-center animate-ios-fade"
                style="animation-delay: 0.15s"
              >
                <div
                  class="chip-scroll-container !py-2 !px-1 md:!px-4 w-full flex items-center justify-start sm:justify-center gap-2 md:gap-3.5 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory touch-pan-x cursor-grab active:cursor-grabbing"
                  style="touch-action: pan-x; -webkit-overflow-scrolling: touch;"
                  #chipScrollContainer
                  (mousedown)="startChipDrag($event, chipScrollContainer)"
                  (mouseleave)="stopChipDrag()"
                  (mouseup)="stopChipDrag()"
                  (mousemove)="onChipDrag($event, chipScrollContainer)"
                  (touchstart)="onChipTouchStart($event)"
                  (touchend)="onChipTouchEnd($event)"
                  role="tablist"
                  aria-label="Category Navigation"
                >
                  @for (cat of hubCategories; track cat.id) {
                    <button
                      type="button"
                      [id]="'hub-chip-' + cat.id"
                      (click)="onHubCategorySelect(cat.id)"
                      class="ios-liquid-chip group !h-10 md:!h-11 !px-4 md:!px-5 !rounded-full transition-all duration-300 active:scale-95 select-none"
                      [class.active]="activeHubCategory() === cat.id"
                      role="tab"
                      [attr.aria-selected]="activeHubCategory() === cat.id"
                    >
                      <app-animated-3d-icon [icon]="cat.icon" variant="3d" class="w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 group-hover:scale-110"></app-animated-3d-icon>
                      <span class="font-semibold text-[13px] md:text-[14px] tracking-wide">{{ cat.label }}</span>
                    </button>
                  }
                </div>
              </div>

              <!-- Shortcuts -->
              <div
                class="mt-8 md:mt-12 w-full grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-4 sm:gap-6 md:gap-8 animate-ios-fade"
                style="animation-delay: 0.3s"
              >
                @for (
                  shortcut of displayedShortcuts();
                  track shortcut.label;
                  let i = $index
                ) {
                  <button
                    type="button"
                    (click)="onShortcutClick(shortcut)"
                    (keydown.enter)="onShortcutClick(shortcut)"
                    class="flex flex-col items-center gap-2 md:gap-3.5 group cursor-pointer border-none bg-transparent p-0 active:scale-95 transition-transform duration-300 relative overflow-visible"
                  >
                    <div class="relative overflow-visible shrink-0">
                      <div
                        class="w-16 h-16 md:w-20 md:h-20 glass-card !rounded-[24px] flex items-center justify-center group-hover:scale-115 group-hover:-translate-y-1.5 group-hover:shadow-[0_16px_36px_rgba(2,132,199,0.32)] group-active:scale-95 transition-all duration-500 border border-white/40 dark:border-white/15 shadow-md"
                        [ngClass]="getShortcutColor(i)"
                      >
                        <div
                          class="absolute inset-0 bg-gradient-to-br from-white/50 via-white/10 to-transparent opacity-30 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-[24px]"
                        ></div>
                        @if (shortcut.label === 'Profile' && authService.isAuthenticated() && authService.userProfile()) {
                          @let profile = authService.userProfile()!;
                          @if (profile.photoURL) {
                            <div class="w-9 h-9 md:w-10 md:h-10 rounded-full overflow-hidden flex items-center justify-center relative z-10 shadow-sm border border-black/10 dark:border-white/20 transition-transform duration-500 group-hover:scale-110">
                              <img
                                [src]="profile.photoURL"
                                [alt]="profile.displayName || 'Profile'"
                                class="w-full h-full object-cover rounded-full"
                                referrerpolicy="no-referrer"
                              />
                            </div>
                          } @else if (profile.initial) {
                            <div
                              class="w-full h-full rounded-full bg-ios-blue text-white flex items-center justify-center text-xl md:text-2xl font-black relative z-10 transition-transform duration-500 group-hover:scale-110 shadow-inner"
                            >
                              {{ profile.initial }}
                            </div>
                          } @else {
                            <app-animated-3d-icon
                              [icon]="shortcut.icon"
                              variant="3d"
                              class="w-9 h-9 md:w-11 md:h-11 relative z-10 transition-transform duration-500 group-hover:scale-110"
                            ></app-animated-3d-icon>
                          }
                        } @else {
                          <app-animated-3d-icon
                            [icon]="shortcut.icon"
                            variant="3d"
                            class="w-9 h-9 md:w-11 md:h-11 relative z-10 transition-transform duration-500 group-hover:scale-110"
                          ></app-animated-3d-icon>
                        }
                      </div>
                    </div>

                    <span
                      class="text-[11px] md:text-[13px] font-bold tracking-[0.04em] text-gray-700 dark:text-zinc-300 group-hover:text-ios-blue dark:group-hover:text-sky-400 transition-all duration-300"
                      >{{ shortcut.label }}</span
                    >
                  </button>
                }
              </div>
            </div>
          }

          <!-- Results Section -->
          @if (hasSearched()) {
            <div
              class="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-24 md:pb-40 space-y-10 md:space-y-16"
              #resultsContainer
            >
              @if (
                webViewUrl() && (category() === "web" || category() === "all")
              ) {
                <div
                  class="animate-item glass-card overflow-hidden !rounded-[40px] border-ios-blue/30 shadow-2xl h-[600px] md:h-[800px] flex flex-col"
                >
                  <div
                    class="bg-white/5 border-b border-white/10 px-6 py-4 flex items-center justify-between"
                  >
                    <div class="flex items-center gap-3">
                      <div
                        class="w-8 h-8 rounded-lg bg-ios-blue/10 flex items-center justify-center"
                      >
                        <mat-icon class="text-ios-blue text-[18px]"
                          >language</mat-icon
                        >
                      </div>
                      <span
                        class="text-ios-caption font-bold truncate max-w-[200px] md:max-w-md"
                        >{{ webViewUrl() }}</span
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        (click)="webViewUrl.set(null)"
                        class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-ios-blue transition-all"
                      >
                        <mat-icon class="text-[20px]">close</mat-icon>
                      </button>
                      <a
                        [href]="webViewUrl()"
                        target="_blank"
                        class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-ios-blue transition-all"
                      >
                        <mat-icon class="text-[20px]">open_in_new</mat-icon>
                      </a>
                    </div>
                  </div>

                  <div
                    class="relative flex-1 bg-white dark:bg-zinc-900 flex flex-col overflow-hidden"
                  >
                    <!-- Fallback/Loading Message -->
                    <div
                      class="absolute inset-0 flex flex-col items-center justify-center p-8 text-center space-y-6 z-0"
                    >
                      <div
                        class="w-24 h-24 bg-ios-blue/5 rounded-full flex items-center justify-center text-ios-blue relative"
                      >
                        <mat-icon
                          class="text-5xl"
                          [class.animate-pulse]="webViewLoading()"
                          >language</mat-icon
                        >
                        @if (webViewLoading()) {
                          <div
                            class="absolute inset-0 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"
                          ></div>
                        }
                      </div>
                      <div class="space-y-2 max-w-sm">
                        <h3 class="text-ios-headline text-xl">
                          {{
                            webViewLoading()
                              ? "Connecting to site..."
                              : "Connection Restricted"
                          }}
                        </h3>
                        <p class="text-ios-caption opacity-60">
                          {{
                            webViewLoading()
                              ? "Please wait while we establish a secure connection."
                              : 'Some websites prevent being embedded for security reasons. If you see a "Refused to connect" error, please open the site in a new tab.'
                          }}
                        </p>
                      </div>
                      <div class="flex items-center gap-3">
                        <a
                          [href]="webViewUrl()"
                          target="_blank"
                          class="ios-button-primary !py-2.5 !px-6 !text-sm flex items-center gap-2 shadow-lg shadow-ios-blue/20"
                        >
                          <mat-icon class="text-[18px]">open_in_new</mat-icon>
                          Open in New Tab
                        </a>
                        @if (!webViewLoading()) {
                          <button
                            (click)="onReloadWebView()"
                            class="ios-button-secondary !py-2.5 !px-6 !text-sm flex items-center gap-2"
                          >
                            <mat-icon class="text-[18px]">refresh</mat-icon>
                            Retry
                          </button>
                        }
                      </div>
                    </div>

                    <!-- The Iframe -->
                    <iframe
                      [src]="safeWebViewUrl()"
                      (load)="onWebViewLoad()"
                      class="relative z-10 flex-1 w-full border-none bg-transparent transition-all duration-1000"
                      [class.opacity-0]="webViewLoading()"
                      [class.pointer-events-none]="webViewLoading()"
                      [class.opacity-100]="!webViewLoading()"
                    ></iframe>
                  </div>
                </div>
              }

              @if (isLoading()) {
                <div class="space-y-6 md:space-y-10">
                  @for (i of [1, 2, 3]; track i) {
                    <div
                      class="glass-card p-6 md:p-10 animate-pulse border-white/20 !rounded-[32px] md:!rounded-[40px]"
                    >
                      <div
                        class="flex items-center gap-4 md:gap-5 mb-6 md:mb-8"
                      >
                        <div
                          class="w-12 h-12 md:w-14 md:h-14 bg-ios-blue/10 rounded-xl md:rounded-2xl"
                        ></div>
                        <div class="space-y-2 md:space-y-3 flex-1">
                          <div
                            class="h-4 md:h-5 bg-ios-blue/10 rounded-full w-1/3"
                          ></div>
                          <div
                            class="h-3 md:h-4 bg-ios-blue/5 rounded-full w-1/4"
                          ></div>
                        </div>
                      </div>
                      <div
                        class="h-6 md:h-8 bg-ios-blue/10 rounded-full w-3/4 mb-4 md:mb-6"
                      ></div>
                      <div
                        class="h-4 md:h-5 bg-ios-blue/5 rounded-full w-full mb-2 md:mb-3"
                      ></div>
                      <div
                        class="h-4 md:h-5 bg-ios-blue/5 rounded-full w-5/6"
                      ></div>
                    </div>
                  }
                </div>
              } @else if (searchService.response(); as res) {
                <!-- Person Results -->
                @if (
                  (category() === "all" || category() === "person") &&
                  persons().length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">person</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Person
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && persons().length > 5 ? '5 of ' + persons().length : persons().length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Person')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Person results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4 md:gap-5 animate-ios-fade">
                      @for (acc of (category() === 'all' ? persons().slice(0, 5) : persons()); track acc.uid) {
                        <app-person-card [account]="acc"></app-person-card>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Person')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Person</span>
                          @if (persons().length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ persons().length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                    @if (category() === 'person' && (hasMorePersons() || isLoadingMorePersons())) {
                      <div class="flex justify-center pt-2 pb-4">
                        <button
                          type="button"
                          (click)="loadMorePersons()"
                          [disabled]="isLoadingMorePersons()"
                          class="px-6 py-2.5 rounded-full bg-ios-blue/10 hover:bg-ios-blue/20 text-ios-blue dark:bg-ios-blue/20 dark:hover:bg-ios-blue/30 font-bold text-xs transition-all duration-300 flex items-center gap-2 cursor-pointer disabled:opacity-50 active:scale-95 shadow-xs"
                        >
                          @if (isLoadingMorePersons()) {
                            <div class="w-4 h-4 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                            <span>Loading Persons...</span>
                          } @else {
                            <mat-icon class="!text-[18px]">expand_more</mat-icon>
                            <span>Load More Persons</span>
                          }
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- Organization Results -->
                @if (
                  (category() === "all" || category() === "organization") &&
                  orgs().length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-purple-500/10 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">corporate_fare</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Organization
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && orgs().length > 5 ? '5 of ' + orgs().length : orgs().length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Organization')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Organization results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4 md:gap-5 animate-ios-fade">
                      @for (acc of (category() === 'all' ? orgs().slice(0, 5) : orgs()); track acc.uid) {
                        <app-organization-card [account]="acc"></app-organization-card>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Organization')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Organization</span>
                          @if (orgs().length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ orgs().length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                    @if (category() === 'organization' && (hasMoreOrgs() || isLoadingMoreOrgs())) {
                      <div class="flex justify-center pt-2 pb-4">
                        <button
                          type="button"
                          (click)="loadMoreOrgs()"
                          [disabled]="isLoadingMoreOrgs()"
                          class="px-6 py-2.5 rounded-full bg-ios-blue/10 hover:bg-ios-blue/20 text-ios-blue dark:bg-ios-blue/20 dark:hover:bg-ios-blue/30 font-bold text-xs transition-all duration-300 flex items-center gap-2 cursor-pointer disabled:opacity-50 active:scale-95 shadow-xs"
                        >
                          @if (isLoadingMoreOrgs()) {
                            <div class="w-4 h-4 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                            <span>Loading Organizations...</span>
                          } @else {
                            <mat-icon class="!text-[18px]">expand_more</mat-icon>
                            <span>Load More Organizations</span>
                          }
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- Country Results -->
                @if (
                  (category() === "all" || category() === "country") &&
                  countries().length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">public</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Country
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && countries().length > 5 ? '5 of ' + countries().length : countries().length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Country')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Country results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4 md:gap-5 animate-ios-fade">
                      @for (acc of (category() === 'all' ? countries().slice(0, 5) : countries()); track acc.uid) {
                        <app-country-card [account]="acc"></app-country-card>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Country')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Country</span>
                          @if (countries().length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ countries().length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- Chats Results -->
                @if (
                  (category() === "all" || category() === "chats") &&
                  ( (res.chats && res.chats.length > 0) || filteredContacts().length > 0 || category() === "chats" )
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">forum</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Chats
                        </h3>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Chats')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Chats results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>

                    @if (category() === 'chats') {
                      <!-- Categorizing Chips for 'Person', 'Organization', and 'Contact' under the 'Chats' section -->
                      <div class="px-2 sm:px-4 md:px-6 flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5">
                        @for (chip of chatFilterChips; track chip.id) {
                          <button
                            type="button"
                            [id]="'chats-subchip-' + chip.id"
                            (click)="chatsSubcategory.set(chip.id)"
                            class="rounded-full text-xs font-semibold flex items-center transition-all duration-300 cursor-pointer border shadow-xs active:scale-95"
                            [class.px-3.5]="chatsSubcategory() === chip.id"
                            [class.py-1.5]="chatsSubcategory() === chip.id"
                          [class.gap-1.5]="chatsSubcategory() === chip.id"
                          [class.p-2]="chatsSubcategory() !== chip.id"
                          [class.bg-ios-blue]="chatsSubcategory() === chip.id"
                          [class.text-white]="chatsSubcategory() === chip.id"
                          [class.border-ios-blue]="chatsSubcategory() === chip.id"
                          [class.shadow-md]="chatsSubcategory() === chip.id"
                          [class.shadow-ios-blue/20]="chatsSubcategory() === chip.id"
                          [class.bg-black/5]="chatsSubcategory() !== chip.id"
                          [class.dark:bg-white/5]="chatsSubcategory() !== chip.id"
                          [class.text-zinc-600]="chatsSubcategory() !== chip.id"
                          [class.dark:text-zinc-400]="chatsSubcategory() !== chip.id"
                          [class.border-black/5]="chatsSubcategory() !== chip.id"
                          [class.dark:border-white/10]="chatsSubcategory() !== chip.id"
                          [class.hover:bg-black/10]="chatsSubcategory() !== chip.id"
                          [class.dark:hover:bg-white/10]="chatsSubcategory() !== chip.id"
                          [title]="chip.label"
                          [attr.aria-label]="chip.label"
                        >
                          <mat-icon class="!text-[18px] leading-none shrink-0">{{ chip.icon }}</mat-icon>
                          @if (chatsSubcategory() === chip.id) {
                            <span class="leading-none pr-0.5">{{ chip.label }}</span>
                          }
                        </button>
                      }
                    </div>
                  }

                    <!-- Contact view when 'Contact' chip is selected -->
                    @if (chatsSubcategory() === 'contact') {
                      <!-- Add Contact Button on the search page -->
                      <div class="px-4 md:px-8 mb-3.5">
                        <button
                          type="button"
                          id="search-add-contact-btn"
                          (click)="openAddContactModal()"
                          class="w-full p-3.5 rounded-2xl border border-black/5 dark:border-white/10 flex items-center justify-between shadow-xs hover:shadow-md transition-all group bg-white/70 dark:bg-zinc-900/70 hover:bg-white/95 dark:hover:bg-zinc-800/95 backdrop-blur-xl cursor-pointer active:scale-[0.99]"
                        >
                          <div class="flex items-center gap-3.5 min-w-0">
                            <div class="w-10 h-10 rounded-full bg-ios-blue/10 dark:bg-ios-blue/20 text-ios-blue flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform shadow-xs">
                              <mat-icon class="!text-[22px]">person_add</mat-icon>
                            </div>
                            <div class="text-left min-w-0">
                              <h4 class="text-sm font-bold text-zinc-900 dark:text-white group-hover:text-ios-blue transition-colors leading-tight">
                                Add Contact
                              </h4>
                              <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                                Search users by 10-digit phone number to add to your contacts
                              </p>
                            </div>
                          </div>

                          <div class="flex items-center gap-2 shrink-0">
                            <span class="text-xs font-bold text-ios-blue hidden sm:inline">Add</span>
                            <div class="w-8 h-8 rounded-full bg-ios-blue/10 text-ios-blue group-hover:bg-ios-blue group-hover:text-white flex items-center justify-center transition-all shadow-xs">
                              <mat-icon class="!text-[18px]">add</mat-icon>
                            </div>
                          </div>
                        </button>
                      </div>

                      @if (filteredContacts().length > 0) {
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 animate-ios-fade">
                          @for (contact of (category() === 'all' ? filteredContacts().slice(0, 5) : filteredContacts()); track contact.id) {
                            <div class="glass-card !rounded-2xl border border-black/5 dark:border-white/10 flex flex-col shadow-xs hover:shadow-md transition-all group bg-white/60 dark:bg-zinc-900/60 backdrop-blur-xl relative overflow-hidden">
                              
                              <!-- Top Row: Contact Info + Pin Icon in Top-Right Corner (or Delete Icon) -->
                              <div class="p-3.5 flex items-center justify-between gap-3 relative">
                                
                                <!-- Contact Avatar & Details -->
                                <div class="flex items-center gap-3.5 min-w-0 flex-1 cursor-pointer" [routerLink]="['/profile', contact.uid]">
                                  <div class="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-tr from-ios-blue/15 to-ios-purple/15 dark:from-ios-blue/25 dark:to-ios-purple/25 shrink-0 border border-white/20 dark:border-white/10 relative shadow-xs">
                                    @if (contact.photoURL) {
                                      <img [src]="contact.photoURL" [alt]="contact.displayName" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" referrerpolicy="no-referrer" />
                                    } @else {
                                      <div class="w-full h-full flex items-center justify-center text-ios-blue font-black text-base">
                                        {{ (contact.displayName || 'U').charAt(0).toUpperCase() }}
                                      </div>
                                    }
                                    @if (contact.isOnline) {
                                      <div class="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white dark:border-zinc-900 z-10"></div>
                                    }
                                  </div>

                                  <div class="min-w-0 flex-1">
                                    <div class="flex items-center gap-1.5 min-w-0">
                                      <h4 class="text-sm font-bold text-zinc-900 dark:text-white truncate group-hover:text-ios-blue transition-colors leading-tight">
                                        {{ contact.displayName }}
                                      </h4>

                                      <!-- Display only the icon itself without circular green background -->
                                      @if (!isMeContact(contact)) {
                                        <mat-icon 
                                          class="!text-[15px] leading-none text-zinc-400 dark:text-zinc-500 shrink-0 select-none" 
                                          [title]="contact.category"
                                        >{{ getContactCategoryIcon(contact.category) }}</mat-icon>
                                      }
                                    </div>
                                    <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5 font-medium">
                                      {{ contact.username ? '@' + contact.username : (contact.email || '@user') }}
                                    </p>
                                  </div>
                                </div>

                                <!-- Top-Right Corner Action: Pin Icon for Own Card or Delete Icon for Contacts -->
                                <div class="flex items-center gap-1 shrink-0">
                                  @if (isMeContact(contact)) {
                                    <div 
                                      class="w-8 h-8 rounded-full bg-ios-blue/10 text-ios-blue flex items-center justify-center shadow-xs border border-ios-blue/20"
                                      title="Pinned to top (You)"
                                    >
                                      <mat-icon class="!text-[18px]">push_pin</mat-icon>
                                    </div>
                                  } @else {
                                    <button
                                      type="button"
                                      (click)="deleteContactFromSearch(contact)"
                                      class="w-8 h-8 rounded-full text-zinc-400 hover:text-ios-red hover:bg-ios-red/10 flex items-center justify-center transition-all cursor-pointer active:scale-90"
                                      title="Delete Contact"
                                    >
                                      <mat-icon class="!text-[18px]">delete</mat-icon>
                                    </button>
                                  }
                                </div>

                              </div>

                              <!-- Bottom Layer Action Row: Simplified Single Line with Voice, Video, and Chat -->
                              <div class="px-3 py-2 bg-black/5 dark:bg-white/5 border-t border-black/5 dark:border-white/5 grid grid-cols-3 gap-1 text-center">
                                <!-- 1. Voice (Inactive) -->
                                <button
                                  type="button"
                                  disabled
                                  class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl text-zinc-400 dark:text-zinc-500 opacity-50 cursor-not-allowed text-[11px] font-medium"
                                  title="Voice feature inactive"
                                >
                                  <mat-icon class="!text-[16px]">call</mat-icon>
                                  <span>Voice</span>
                                </button>

                                <!-- 2. Video (Inactive) -->
                                <button
                                  type="button"
                                  disabled
                                  class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl text-zinc-400 dark:text-zinc-500 opacity-50 cursor-not-allowed text-[11px] font-medium"
                                  title="Video feature inactive"
                                >
                                  <mat-icon class="!text-[16px]">videocam</mat-icon>
                                  <span>Video</span>
                                </button>

                                <!-- 3. Chat (Active) -->
                                <button
                                  type="button"
                                  (click)="startChatWithContact(contact)"
                                  class="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-xl bg-ios-blue/10 hover:bg-ios-blue text-ios-blue hover:text-white font-bold text-[11px] transition-all active:scale-95 cursor-pointer disabled:opacity-50 shadow-xs"
                                  title="Open Chat"
                                >
                                  <mat-icon class="!text-[16px]">chat</mat-icon>
                                  <span>Chat</span>
                                </button>
                              </div>

                            </div>
                          }
                        </div>
                      } @else {
                        <div class="py-12 px-4 text-center glass-card !rounded-2xl border border-black/5 dark:border-white/10 my-4 bg-white/40 dark:bg-black/40 backdrop-blur-xl">
                          <mat-icon class="!text-[36px] text-zinc-400 mb-2">contacts</mat-icon>
                          <p class="text-xs font-bold text-zinc-500 mb-3">No contacts found</p>
                          <button
                            type="button"
                            (click)="openAddContactModal()"
                            class="px-5 py-2.5 rounded-full bg-ios-blue text-white font-bold text-xs shadow-md shadow-ios-blue/20 hover:scale-105 active:scale-95 transition-all inline-flex items-center gap-2 cursor-pointer"
                          >
                            <mat-icon class="!text-[18px]">person_add</mat-icon>
                            <span>Add Contact</span>
                          </button>
                        </div>
                      }
                    } @else {
                      <!-- Display Chats (Filtered by Person vs Organization) -->
                      @let activeChats = displayedChats();
                      @if (activeChats.length > 0) {
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4 md:gap-5">
                          @for (chat of (category() === 'all' ? activeChats.slice(0, 5) : activeChats); track chat?.id) {
                            @if (
                              chat.lastMessage &&
                              chat.lastMessage !== "No messages yet"
                            ) {
                              <div
                                class="animate-item group glass-card p-4 !rounded-[20px] border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 flex items-center gap-4 cursor-pointer relative overflow-hidden"
                                (click)="openChatResult(chat)"
                                (keydown.enter)="openChatResult(chat)"
                                tabindex="0"
                              >
                                <div
                                  class="absolute inset-0 bg-gray-50 dark:bg-gray-800/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                ></div>

                                <div
                                  class="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden bg-ios-blue/10 shrink-0 border border-black/5 dark:border-white/10 relative z-10 shadow-sm"
                                >
                                  @if (chat?.participantPhoto) {
                                    <img
                                      [src]="chat.participantPhoto"
                                      [alt]="chat.participantName"
                                      class="w-full h-full object-cover transition-transform duration-500"
                                      referrerpolicy="no-referrer"
                                    />
                                  } @else {
                                    <div
                                      class="w-full h-full flex items-center justify-center text-ios-blue/60 text-lg font-medium"
                                    >
                                      {{
                                        (chat?.participantName || "?")
                                          .charAt(0)
                                          .toUpperCase()
                                      }}
                                    </div>
                                  }
                                  <!-- Online Status Indicator -->
                                  <div
                                    class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border border-white dark:border-dark-surface z-20"
                                    [class.bg-green-500]="$any(chat).isOnline"
                                    [class.bg-gray-400]="!$any(chat).isOnline"
                                  ></div>
                                </div>
                                <div class="flex-1 min-w-0 relative z-10">
                                  <h4
                                    class="text-base font-semibold truncate group-hover:text-ios-blue transition-colors"
                                  >
                                    {{
                                      (chat?.participantName || "").charAt(0).toUpperCase() +
                                      (chat?.participantName || "").substring(1).toLowerCase()
                                    }}
                                  </h4>
                                  <p
                                    class="text-[12px] md:text-[13px] text-gray-500 truncate italic"
                                  >
                                    "{{ chat.lastMessage || "No messages yet" }}"
                                  </p>
                                  <div class="mt-1 flex items-center gap-2">
                                    <span
                                      class="text-[11px] font-medium text-gray-700 dark:text-gray-300"
                                    >
                                      {{
                                        formatDate(chat.updatedAt) | date: "short"
                                      }}
                                    </span>
                                  </div>
                                </div>
                                <div class="flex items-center gap-1 relative z-20 shrink-0">
                                  <button
                                    type="button"
                                    (click)="deleteChatFromSearch($event, chat)"
                                    class="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-ios-red hover:bg-ios-red/10 transition-colors cursor-pointer"
                                    title="Delete Chat"
                                    aria-label="Delete Chat"
                                  >
                                    <mat-icon class="!text-[18px]">delete</mat-icon>
                                  </button>
                                  <mat-icon
                                    class="text-gray-400 group-hover:text-ios-blue transition-all duration-300"
                                    >chevron_right</mat-icon
                                  >
                                </div>
                              </div>
                            }
                          }
                        </div>
                      } @else {
                        <div class="py-12 px-4 text-center glass-card !rounded-2xl border border-black/5 dark:border-white/10 my-4 bg-white/40 dark:bg-black/40 backdrop-blur-xl">
                          <mat-icon class="!text-[36px] text-zinc-400 mb-2">forum</mat-icon>
                          <p class="text-xs font-bold text-zinc-500">No {{ chatsSubcategory() }} chats found</p>
                        </div>
                      }
                    }

                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Chats')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Chats</span>
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- News Results -->
                @if (
                  (category() === "all" || category() === "news") &&
                  res.news &&
                  res.news.length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-rose-500/10 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">newspaper</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          News
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && res.news.length > 5 ? '5 of ' + res.news.length : res.news.length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('News')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all News results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    <div
                      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
                    >
                      @for (newsItem of (category() === 'all' ? res.news.slice(0, 5) : res.news); track newsItem.id) {
                        <div
                          class="animate-item group p-0 rounded-[32px] overflow-hidden relative cursor-pointer outline-none touch-optimized-card bg-white/20 dark:bg-black/20 backdrop-blur-3xl border border-white/40 dark:border-white/10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] transition-all duration-500 shadow-ios flex flex-col"
                          [routerLink]="['/news', newsItem.ownerUid || newsItem.authorUid || newsItem.ownedBy || 'system', newsItem.id]"
                          tabindex="0"
                        >
                          <!-- Glass glare effect -->
                          <div class="absolute inset-0 bg-gradient-to-br from-white/30 via-white/5 to-transparent dark:from-white/10 dark:via-transparent dark:to-transparent opacity-100 pointer-events-none rounded-[32px] z-10 transition-opacity duration-500"></div>

                          <!-- News Thumbnail -->
                          <div
                            class="h-48 relative overflow-hidden bg-black/5 dark:bg-white/5"
                          >
                            <img
                              [src]="newsItem.bannerImage || 'https://picsum.photos/seed/' + newsItem.id + '/400/300'"
                              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 opacity-90 group-hover:opacity-100"
                              alt="News Image"
                              referrerpolicy="no-referrer"
                            />
                            <!-- Category Tag -->
                            <div
                              class="absolute top-4 left-4 px-3 py-1 rounded-2xl bg-white/40 dark:bg-black/40 backdrop-blur-[20px] text-black dark:text-white text-[10px] font-black uppercase tracking-widest border border-white/40 shadow-sm z-20"
                            >
                              {{ newsItem.category || 'News' }}
                            </div>

                            <!-- Premium Badges -->
                            <div class="absolute top-4 right-4 flex items-center gap-1 z-20">
                              @if (newsItem.status) {
                                <span 
                                  [class]="
                                    newsItem.status === 'published'
                                      ? 'px-2 py-0.5 rounded-full bg-emerald-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                      : 'px-2 py-0.5 rounded-full bg-zinc-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                  "
                                >
                                  {{ newsItem.status }}
                                </span>
                              }
                              @if (newsItem.verificationStatus) {
                                <span 
                                  [class]="
                                    newsItem.verificationStatus === 'approved'
                                      ? 'px-2 py-0.5 rounded-full bg-emerald-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                      : newsItem.verificationStatus === 'rejected'
                                        ? 'px-2 py-0.5 rounded-full bg-rose-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                        : 'px-2 py-0.5 rounded-full bg-amber-500/80 text-white text-[8px] font-black uppercase tracking-wider'
                                  "
                                >
                                  {{ newsItem.verificationStatus }}
                                </span>
                              }
                            </div>
                            
                            <!-- Uploader Avatar overlay -->
                            <div class="absolute bottom-4 right-4 w-10 h-10 rounded-full border-2 border-white/80 dark:border-zinc-800 shadow-xl overflow-hidden bg-white/50 backdrop-blur-md z-20">
                               <!-- Assuming we don't fetch full user profile, show a fallback avatar if we don't have user pic -->
                               <div class="w-full h-full flex items-center justify-center bg-ios-blue/80 text-white font-bold text-xs uppercase">
                                 {{ (newsItem.ownerUid || '?').charAt(0) }}
                               </div>
                            </div>
                            
                            <div
                              class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-100 z-10"
                            ></div>
                          </div>

                          <div class="p-6 flex flex-col flex-1 relative z-20">
                            <h4
                              class="text-[18px] font-bold tracking-tight text-gray-900 dark:text-white line-clamp-2 leading-tight mb-2 group-hover:text-ios-blue transition-colors duration-300"
                            >
                              {{ newsItem.title }}
                            </h4>
                            <p
                              class="text-gray-600 dark:text-gray-300 line-clamp-3 text-sm flex-1 leading-relaxed mb-4"
                            >
                              {{ newsItem.description || newsItem.content }}
                            </p>

                            <div
                              class="flex items-center justify-between pt-4 border-t border-black/5 dark:border-white/10"
                            >
                              <div
                                class="flex items-center gap-4 text-[12px] font-semibold text-gray-500 dark:text-gray-400"
                              >
                                <span
                                  class="flex items-center gap-1.5"
                                  ><mat-icon class="text-[16px] text-ios-blue opacity-80"
                                    >visibility</mat-icon
                                  >
                                  {{ (newsItem.viewedBy.length || 0) | countFormat }}</span
                                >
                                <span
                                  class="flex items-center gap-1.5"
                                  ><mat-icon class="text-[16px] text-ios-pink opacity-80"
                                    >favorite</mat-icon
                                  >
                                  {{ (newsItem.likedBy.length || 0) | countFormat }}</span
                                >
                              </div>
                              <span
                                class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.15em]"
                                >{{
                                  formatDate(newsItem.createdAt) | date: "MMM d"
                                }}</span
                              >
                            </div>
                            <!-- Status Badges for Owners and Master Admin -->
                            @if (newsItem.ownerUid === authService.user()?.uid || authService.isMasterAdmin()) {
                              <div class="flex items-center gap-1.5 mt-3 select-none">
                                @if (newsItem.status === 'published') {
                                  <span class="px-2 py-0.5 rounded bg-green-500 text-white text-[9px] font-black uppercase tracking-wider">
                                    Published
                                  </span>
                                } @else {
                                  <span class="px-2 py-0.5 rounded bg-gray-400 text-white text-[9px] font-black uppercase tracking-wider">
                                    Unpublished
                                  </span>
                                }

                                @if (newsItem.verificationStatus === 'review' || !newsItem.verificationStatus) {
                                  <span class="px-2 py-0.5 rounded bg-amber-500 text-white text-[9px] font-black uppercase tracking-wider">
                                    Review
                                  </span>
                                } @else if (newsItem.verificationStatus === 'approved') {
                                  <span class="px-2 py-0.5 rounded bg-green-600 text-white text-[9px] font-black uppercase tracking-wider">
                                    Approved
                                  </span>
                                } @else if (newsItem.verificationStatus === 'rejected') {
                                  <span class="px-2 py-0.5 rounded bg-red-500 text-white text-[9px] font-black uppercase tracking-wider">
                                    Rejected
                                  </span>
                                }
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('News')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More News</span>
                          @if (res.news.length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ res.news.length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- User Images (Instagram Style Profile Grid in Search Results) -->
                @if (
                  (category() === "all" || category() === "images") &&
                  res.userImages &&
                  res.userImages.length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-pink-500/10 dark:bg-pink-500/20 text-pink-600 dark:text-pink-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">image</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Images
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && res.userImages.length > 5 ? '5 of ' + res.userImages.length : res.userImages.length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Images')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Images results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    
                    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4 px-2 sm:px-4 md:px-6">
                      @for (img of (category() === 'all' ? res.userImages.slice(0, 5) : res.userImages); track img.id) {
                        <div 
                          (click)="openImagePopup(img)"
                          (keydown.enter)="openImagePopup(img)"
                          tabindex="0"
                          class="touch-optimized-card group relative aspect-square overflow-hidden rounded-xl md:rounded-2xl border border-black/5 dark:border-white/5 bg-gray-100 dark:bg-zinc-805 shadow-sm focus:outline-none focus:ring-2 focus:ring-ios-blue"
                        >
                          <img 
                            [src]="img.imageUrl" 
                            alt="Approved user gallery image" 
                            class="w-full h-full object-cover transition-transform duration-700"
                            referrerpolicy="no-referrer"
                          />
                          <!-- Image details overlay on hover -->
                          <div class="card-overlay absolute inset-0 bg-black/50 opacity-0 transition-opacity duration-300 flex flex-col justify-between p-2 md:p-3 text-white">
                            <!-- Top row: metadata -->
                            <div class="flex justify-between items-start w-full">
                              <span class="bg-black/40 backdrop-blur-md px-1.5 py-0.5 rounded text-[8px] md:text-[9px] uppercase font-bold tracking-wider">
                                {{ img.visibility || 'public' }}
                              </span>
                              <div class="flex flex-col items-end gap-1 select-none text-right">
                                @if (img.verificationStatus) {
                                  <span 
                                    [class]="
                                      img.verificationStatus === 'approved'
                                        ? 'px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[8px] font-black uppercase tracking-wider'
                                        : img.verificationStatus === 'rejected'
                                          ? 'px-1.5 py-0.5 rounded bg-rose-500 text-white text-[8px] font-black uppercase tracking-wider'
                                          : 'px-1.5 py-0.5 rounded bg-amber-500 text-white text-[8px] font-black uppercase tracking-wider'
                                    "
                                  >
                                    {{ img.verificationStatus }}
                                  </span>
                                }
                              </div>
                            </div>

                            <!-- Action interactives overlaid on card center -->
                            <div class="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center gap-3 md:gap-6">
                              <!-- Heart Button -->
                              <button 
                                type="button"
                                (click)="toggleLike($event, img)"
                                class="flex flex-col items-center gap-0.5 cursor-pointer transition-transform hover:scale-115 active:scale-90 bg-transparent border-none p-0"
                              >
                                <mat-icon 
                                  [class.text-ios-red]="(img.likedBy || img.likes || []).includes(authService.user()?.uid || '')"
                                  class="text-white drop-shadow !text-lg md:!text-2xl h-5 md:h-6 w-5 md:w-6"
                                >
                                  {{ (img.likedBy || img.likes || []).includes(authService.user()?.uid || '') ? 'favorite' : 'favorite_border' }}
                                </mat-icon>
                                <span class="text-[10px] md:text-sm font-black drop-shadow tracking-wider">
                                  {{ (img.likedBy.length || 0) | countFormat }}
                                </span>
                              </button>

                              <!-- Views Indicator -->
                              <div class="flex flex-col items-center gap-0.5 text-white">
                                <mat-icon class="drop-shadow !text-lg md:!text-2xl h-5 md:h-6 w-5 md:w-6">visibility</mat-icon>
                                <span class="text-[10px] md:text-sm font-black drop-shadow tracking-wider">
                                  {{ (img.viewedBy.length || 0) | countFormat }}
                                </span>
                              </div>
                            </div>

                            <!-- Bottom tag -->
                            <div class="text-right w-full">
                              <span class="text-[8px] md:text-[9px] opacity-60 tracking-widest uppercase">Approved</span>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Images')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Images</span>
                          @if (res.userImages.length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ res.userImages.length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- Jobs Results -->
                @if (
                  (category() === "all" || category() === "jobs") &&
                  jobs() &&
                  jobs().length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">work</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Jobs
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && jobs().length > 5 ? '5 of ' + jobs().length : jobs().length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Jobs')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Jobs results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-6 px-2 sm:px-4 md:px-6">
                      @for (job of (category() === 'all' ? jobs().slice(0, 5) : jobs()); track job.id) {
                        <div [routerLink]="['/job', job.ownerUid || job.ownedBy || job.authorUid || job.uploadedBy || 'system', job.id]" tabindex="0" (keydown.enter)="router.navigate(['/job', job.ownerUid || job.ownedBy || job.authorUid || job.uploadedBy || 'system', job.id])" class="animate-item group p-6 rounded-[32px] overflow-hidden relative cursor-pointer outline-none touch-optimized-card bg-white/20 dark:bg-black/20 backdrop-blur-3xl border border-white/40 dark:border-white/10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] transition-all duration-300 flex flex-col justify-between">
                          <div class="space-y-4">
                            <div class="flex items-start justify-between">
                              <div class="flex items-center gap-4">
                                <div class="w-12 h-12 rounded-2xl bg-ios-blue/10 flex items-center justify-center text-ios-blue font-bold text-lg select-none overflow-hidden">
                                  @if (job.companyLogoUrl) {
                                    <img [src]="job.companyLogoUrl" class="w-full h-full object-cover" alt="Company Logo" referrerpolicy="no-referrer" />
                                  } @else {
                                    {{ (job.jobTitle || 'J').charAt(0).toUpperCase() }}
                                  }
                                </div>
                                <div>
                                  <h4 class="text-[17px] font-bold tracking-tight text-gray-900 dark:text-white group-hover:text-ios-blue transition-colors leading-snug">
                                    {{ job.jobTitle }}
                                  </h4>
                                  <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                    {{ job.companyInfo || 'Verified Partner' }}
                                  </p>
                                </div>
                              </div>
                              <div class="flex flex-col items-end gap-1 select-none">
                                <span class="px-3 py-1 rounded-full bg-ios-green/10 text-ios-green text-[10px] font-black uppercase tracking-widest border border-ios-green/25">
                                  Verified
                                </span>
                                <div class="flex items-center gap-1 flex-wrap justify-end">
                                  @if (job.status) {
                                    <span
                                      [class]="
                                        job.status === 'published'
                                          ? 'px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[8px] font-black uppercase tracking-wider'
                                          : 'px-1.5 py-0.5 rounded bg-zinc-500/10 text-zinc-400 border border-zinc-500/20 text-[8px] font-black uppercase tracking-wider'
                                      "
                                    >
                                      {{ job.status }}
                                    </span>
                                  }
                                  @if (job.verificationStatus) {
                                    <span
                                      [class]="
                                        job.verificationStatus === 'approved'
                                          ? 'px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[8px] font-black uppercase tracking-wider'
                                          : job.verificationStatus === 'rejected'
                                            ? 'px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-500 border border-rose-500/20 text-[8px] font-black uppercase tracking-wider'
                                            : 'px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[8px] font-black uppercase tracking-wider'
                                      "
                                    >
                                      {{ job.verificationStatus }}
                                    </span>
                                  }
                                </div>
                              </div>
                            </div>

                            <p class="text-[13px] text-gray-600 dark:text-gray-300 line-clamp-3 leading-relaxed">
                              {{ job.description }}
                            </p>

                            @if (job.requirements && job.requirements.length > 0) {
                              <div class="flex flex-wrap gap-2 pt-2">
                                @for (req of job.requirements.slice(0, 3); track req) {
                                  <span class="px-2.5 py-1 rounded-lg bg-black/5 dark:bg-white/5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                                    {{ req }}
                                  </span>
                                }
                              </div>
                            }
                            <!-- Status Badges for Owners and Master Admin -->
                            @if (job.ownerUid === authService.user()?.uid || authService.isMasterAdmin()) {
                              <div class="flex items-center gap-1.5 mt-3 select-none">
                                @if (job.status === 'published') {
                                  <span class="px-2 py-0.5 rounded bg-green-500 text-white text-[9px] font-black uppercase tracking-wider">
                                    Published
                                  </span>
                                } @else {
                                  <span class="px-2 py-0.5 rounded bg-gray-400 text-white text-[9px] font-black uppercase tracking-wider">
                                    Unpublished
                                  </span>
                                }

                                @if (job.verificationStatus === 'review' || !job.verificationStatus) {
                                  <span class="px-2 py-0.5 rounded bg-amber-500 text-white text-[9px] font-black uppercase tracking-wider">
                                    Review
                                  </span>
                                } @else if (job.verificationStatus === 'approved') {
                                  <span class="px-2 py-0.5 rounded bg-green-600 text-white text-[9px] font-black uppercase tracking-wider">
                                    Approved
                                  </span>
                                } @else if (job.verificationStatus === 'rejected') {
                                  <span class="px-2 py-0.5 rounded bg-red-500 text-white text-[9px] font-black uppercase tracking-wider">
                                    Rejected
                                  </span>
                                }
                              </div>
                            }
                          </div>

                          <div class="flex items-center justify-between mt-6 pt-4 border-t border-black/5 dark:border-white/10 text-xs text-gray-500 dark:text-gray-400 font-semibold select-none">
                            <div class="flex items-center gap-3">
                              <span class="flex items-center gap-1.5">
                                <mat-icon class="text-[14px] text-gray-400">location_on</mat-icon>
                                {{ job.location || 'Remote' }}
                              </span>
                              @if (job.salaryRange) {
                                <span class="flex items-center gap-1.5 text-ios-green">
                                  <mat-icon class="text-[14px]">payments</mat-icon>
                                  {{ job.salaryRange }}
                                </span>
                              }
                            </div>
                            <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                              {{ formatDate(job.createdAt) | date: 'MMM d, y' }}
                            </span>
                          </div>
                        </div>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Jobs')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Jobs</span>
                          @if (jobs().length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ jobs().length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- Products Results -->
                @if (
                  (category() === "all" || category() === "products") &&
                  products() &&
                  products().length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">shopping_bag</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Products
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && products().length > 5 ? '5 of ' + products().length : products().length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Products')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Products results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 px-2 sm:px-4 md:px-6">
                      @for (product of (category() === 'all' ? products().slice(0, 5) : products()); track product.id) {
                        <div [routerLink]="['/product', product.ownerUid || product.ownedBy || product.authorUid || product.uploadedBy || 'system', product.id]" tabindex="0" (keydown.enter)="router.navigate(['/product', product.ownerUid || product.ownedBy || product.authorUid || product.uploadedBy || 'system', product.id])" class="animate-item group p-0 rounded-[32px] overflow-hidden relative cursor-pointer outline-none touch-optimized-card bg-white/20 dark:bg-black/20 backdrop-blur-3xl border border-white/40 dark:border-white/10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] transition-all duration-300 flex flex-col justify-between">
                          <div class="relative aspect-video overflow-hidden bg-gray-100 dark:bg-zinc-900 rounded-t-[32px]">
                            @if (product.discount) {
                              <span class="absolute top-4 left-4 z-20 px-2.5 py-1 rounded-xl bg-ios-red text-white text-[10px] font-black uppercase tracking-wider shadow-sm animate-pulse">
                                {{ product.discount }}% OFF
                              </span>
                            }
                            
                            <img
                              [src]="product.imageUrl || (product.images && product.images.length > 0 ? product.images[0] : '') || 'https://picsum.photos/seed/' + product.id + '/500/400'"
                              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                              alt="Product Image"
                              referrerpolicy="no-referrer"
                            />
                          </div>

                          <div class="p-6 flex flex-col flex-1 pb-4">
                            <span class="text-[10px] font-black uppercase tracking-widest text-ios-blue mb-1">
                              {{ product.category || 'Lifestyle' }}
                            </span>
                            <h4 class="text-base font-bold text-gray-900 dark:text-white line-clamp-1 mb-2 group-hover:text-ios-blue transition-colors">
                              {{ product.productName }}
                            </h4>
                            <p class="text-[12px] text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed mb-4 flex-1">
                              {{ product.description }}
                            </p>

                            <!-- Status Badges for All -->
                            <div class="flex items-center gap-1.5 mb-4 select-none">
                              @if (product.status) {
                                <span 
                                  [class]="
                                    product.status === 'published'
                                      ? 'px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[9px] font-black uppercase tracking-wider'
                                      : 'px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-400 border border-zinc-500/20 text-[9px] font-black uppercase tracking-wider'
                                  "
                                >
                                  {{ product.status }}
                                </span>
                              }
                              @if (product.verificationStatus) {
                                <span 
                                  [class]="
                                    product.verificationStatus === 'approved'
                                      ? 'px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[9px] font-black uppercase tracking-wider'
                                      : product.verificationStatus === 'rejected'
                                        ? 'px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20 text-[9px] font-black uppercase tracking-wider'
                                        : 'px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[9px] font-black uppercase tracking-wider'
                                  "
                                >
                                  {{ product.verificationStatus }}
                                </span>
                              }
                            </div>

                            <div class="flex items-center justify-between pt-4 border-t border-black/5 dark:border-white/10">
                              <div class="flex items-baseline gap-1.5">
                                <span class="text-lg font-black text-gray-900 dark:text-white">{{ '$' }}{{ product.price }}</span>
                                @if (product.discount) {
                                  <span class="text-[11px] line-through text-gray-400 font-medium">{{ '$' }}{{ (product.price * (100 / (100 - product.discount))).toFixed(0) }}</span>
                                }
                              </div>
                              <span class="text-[10px] font-extrabold uppercase tracking-widest text-gray-400">
                                Stock: {{ product.stock || 0 }}
                              </span>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Products')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Products</span>
                          @if (products().length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ products().length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- Videos Results -->
                @if (
                  (category() === "all" || category() === "videos") &&
                  videos() &&
                  videos().length > 0
                ) {
                  <div class="space-y-4 md:space-y-6">
                    <div class="flex items-center justify-between px-2 sm:px-4 md:px-6">
                      <div class="flex items-center gap-2.5">
                        <div class="w-8 h-8 rounded-xl bg-violet-500/10 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 flex items-center justify-center shrink-0">
                          <mat-icon class="!text-[18px]">videocam</mat-icon>
                        </div>
                        <h3 class="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] font-black text-gray-900 dark:text-white">
                          Videos
                        </h3>
                        <span class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-black/5 dark:bg-white/10 text-gray-600 dark:text-zinc-300">
                          {{ category() === 'all' && videos().length > 5 ? '5 of ' + videos().length : videos().length }}
                        </span>
                      </div>
                      <div class="h-px flex-1 bg-black/5 dark:bg-white/5 mx-3 md:mx-6"></div>
                      <button
                        type="button"
                        (click)="onCategorySelect('Videos')"
                        class="group inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-ios-blue hover:bg-ios-blue/10 dark:hover:bg-ios-blue/20 transition-all duration-200 cursor-pointer shrink-0 active:scale-95"
                        title="View all Videos results"
                      >
                        <span>More</span>
                        <mat-icon class="!text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</mat-icon>
                      </button>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 px-2 sm:px-4 md:px-6">
                      @for (video of (category() === 'all' ? videos().slice(0, 5) : videos()); track video.id) {
                        <div [routerLink]="['/video', video.ownerUid || video.ownedBy || video.authorUid || video.uploadedBy || 'system', video.id]" tabindex="0" (keydown.enter)="router.navigate(['/video', video.ownerUid || video.ownedBy || video.authorUid || video.uploadedBy || 'system', video.id])" class="animate-item group p-0 rounded-[32px] overflow-hidden relative cursor-pointer outline-none touch-optimized-card bg-white/20 dark:bg-black/20 backdrop-blur-3xl border border-white/40 dark:border-white/10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] hover:shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] transition-all duration-300 flex flex-col justify-between">
                          <div class="relative aspect-video overflow-hidden bg-black rounded-t-[32px]">
                            <img
                              [src]="video.posterUrl || video.thumbnailUrl || 'https://picsum.photos/seed/' + video.id + '/640/360'"
                              class="w-full h-full object-cover opacity-85 transition-transform duration-700 group-hover:scale-105"
                              alt="Video Thumbnail"
                              referrerpolicy="no-referrer"
                            />
                            
                            <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                              <div class="w-14 h-14 rounded-full bg-ios-blue/90 text-white flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                                <mat-icon class="!text-3xl h-8 w-8 leading-none">play_arrow</mat-icon>
                              </div>
                            </div>

                            @if (video.duration) {
                              <span class="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-black/70 backdrop-blur-sm text-white text-[10px] font-bold font-mono">
                                {{ video.duration }}
                              </span>
                            }
                          </div>

                          <div class="p-6">
                            <span class="text-[10px] font-black uppercase tracking-widest text-ios-blue mb-1 block">
                              {{ video.videoCategory || 'Entertainment' }}
                            </span>
                            <h4 class="text-sm font-bold text-gray-900 dark:text-white line-clamp-2 leading-snug mb-2 group-hover:text-ios-blue transition-colors">
                              {{ video.title }}
                            </h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed mb-4">
                              {{ video.description }}
                            </p>

                            <!-- Status Badges for All -->
                            <div class="flex items-center gap-1.5 mb-4 select-none">
                              @if (video.status) {
                                <span 
                                  [class]="
                                    video.status === 'published'
                                      ? 'px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[9px] font-black uppercase tracking-wider'
                                      : 'px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-400 border border-zinc-500/20 text-[9px] font-black uppercase tracking-wider'
                                  "
                                >
                                  {{ video.status }}
                                </span>
                              }
                              @if (video.verificationStatus) {
                                <span 
                                  [class]="
                                    video.verificationStatus === 'approved'
                                      ? 'px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[9px] font-black uppercase tracking-wider'
                                      : video.verificationStatus === 'rejected'
                                        ? 'px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20 text-[9px] font-black uppercase tracking-wider'
                                        : 'px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[9px] font-black uppercase tracking-wider'
                                  "
                                >
                                  {{ video.verificationStatus }}
                                </span>
                              }
                            </div>

                            <div class="flex items-center justify-between pt-4 border-t border-black/5 dark:border-white/10 text-[11px] text-gray-400">
                              <span class="flex items-center gap-1 font-semibold text-ios-blue/80">
                                <mat-icon class="text-[14px]">videocam</mat-icon>
                                Watch Video
                              </span>
                              <div class="flex items-center gap-2">
                                <span>{{ (video.viewedBy.length || 0) | countFormat }} views</span>
                                <span>•</span>
                                <span>{{ formatDate(video.createdAt) | date: 'MMM d' }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                    @if (category() === 'all') {
                      <div class="flex justify-center pt-1 pb-2">
                        <button
                          type="button"
                          (click)="onCategorySelect('Videos')"
                          class="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-ios-blue hover:text-white dark:hover:bg-ios-blue dark:hover:text-white text-zinc-700 dark:text-zinc-200 border border-black/5 dark:border-white/10 font-bold text-xs transition-all duration-300 cursor-pointer shadow-xs active:scale-95"
                        >
                          <span>More Videos</span>
                          @if (videos().length > 5) {
                            <span class="px-2 py-0.5 rounded-full bg-ios-blue/10 dark:bg-white/10 group-hover/btn:bg-white/20 text-[10px] font-black text-ios-blue dark:text-sky-300 group-hover/btn:text-white">
                              +{{ videos().length - 5 }}
                            </span>
                          }
                          <mat-icon class="!text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                <!-- Web Results (Wikipedia Search Integration) -->
                @if (category() === "web") {
                  <app-wikipedia-search [queryInput]="query()"></app-wikipedia-search>
                }

                @if (!hasResultsForCurrentCategory()) {
                  <div
                    class="flex flex-col items-center justify-center py-32 text-center animate-ios-fade"
                  >
                    <div
                      class="w-24 h-24 rounded-[32px] bg-black/5 dark:bg-white/5 flex items-center justify-center text-ios-subtext mb-8 shadow-inner"
                    >
                      <mat-icon class="text-[48px] opacity-20"
                        >search_off</mat-icon
                      >
                    </div>
                    <h3
                      class="text-ios-title !text-2xl font-black tracking-tight mb-3"
                    >
                      No Results Found
                    </h3>
                    <p class="text-ios-body opacity-40 max-w-md mx-auto">
                      We couldn't find anything matching your query in the "{{
                        category() | titlecase
                      }}" category. Try broadening your search or exploring
                      other categories.
                    </p>
                    <button
                      (click)="onReset()"
                      class="mt-10 ios-button-secondary"
                    >
                      Clear Search
                    </button>
                  </div>
                }
              }
            </div>
          }


        </main>

        <!-- Full-screen Premium Image Preview Modal -->
        @if (isImagePopupOpen() && selectedImage()) {
          @let selectedImg = selectedImage();
          @if (selectedImg) {
            <div 
              class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs transition-opacity duration-300 focus:outline-none"
              (click)="onBackdropClick($event)"
              (keydown.escape)="closeImagePopup()"
              tabindex="0"
            >
              <!-- Close button on far top-right -->
              <button 
                type="button"
                (click)="closeImagePopup()"
                class="absolute top-6 right-6 text-white/70 hover:text-white transition-colors bg-black/40 hover:bg-black/60 p-2.5 rounded-full z-10 cursor-pointer"
              >
                <mat-icon class="!text-[28px] h-7 w-7">close</mat-icon>
              </button>

              <!-- Modal Content Box -->
              <div 
                class="relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-zinc-900 rounded-3xl overflow-y-auto shadow-2xl origin-center border border-black/10 dark:border-white/10 flex flex-col md:flex-row"
              >
                
                <!-- Left half: Image container -->
                <div class="flex-1 bg-black flex items-center justify-center relative min-h-[300px] md:min-h-[500px]">
                  <img 
                    [src]="selectedImg.imageUrl" 
                    alt="Full size cropped view" 
                    class="w-full h-full object-cover object-center"
                    referrerpolicy="no-referrer"
                  />
                  <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none"></div>
                </div>

                <!-- Right half: Engagement Bar & Information -->
                <div class="w-full md:w-80 flex flex-col justify-between p-6 bg-white dark:bg-zinc-900">
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <div class="bg-ios-blue/10 text-ios-blue dark:bg-ios-blue/20 p-2.5 rounded-2xl">
                        <mat-icon class="!text-[22px] h-[22px] w-[22px]">verified_user</mat-icon>
                      </div>
                      <div>
                        <h4 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Asset Details</h4>
                        @if (selectedImg.remark || selectedImg.remarks) {
                          <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ selectedImg.remark || selectedImg.remarks }}</p>
                        }
                      </div>
                    </div>

                    <div class="border-t border-black/5 dark:border-white/5 my-4"></div>

                    <!-- Profiles hydration block -->
                    <div class="space-y-4">
                      <!-- Uploaded By Section -->
                      <div class="space-y-1.5">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">Uploaded By</span>
                        @if (isLoadingDetails()) {
                          <div class="flex items-center gap-2.5 animate-pulse">
                            <div class="w-8 h-8 rounded-full bg-black/10 dark:bg-white/10"></div>
                            <div class="flex-1 space-y-1">
                              <div class="h-3 w-20 bg-black/10 dark:bg-white/10 rounded"></div>
                              <div class="h-2 w-12 bg-black/10 dark:bg-white/10 rounded"></div>
                            </div>
                          </div>
                        } @else if (uploaderAccountDetails()) {
                          @let uploader = uploaderAccountDetails();
                          <div 
                            (click)="uploader ? navigateToProfile(uploader.uid) : null" 
                            (keydown.enter)="uploader ? navigateToProfile(uploader.uid) : null"
                            tabindex="0"
                            class="flex items-center gap-2.5 cursor-pointer group hover:bg-black/5 dark:hover:bg-white/5 p-1.5 rounded-xl transition-all outline-none focus:ring-2 focus:ring-ios-blue"
                          >
                            <img 
                              [src]="uploader?.photoURL || 'https://picsum.photos/seed/user/100/100'" 
                              alt="Uploader identity" 
                              class="w-9 h-9 rounded-full object-cover border border-black/10 dark:border-white/10"
                              referrerpolicy="no-referrer"
                            />
                            <div class="min-w-0 flex-1">
                              <h5 class="text-xs font-bold text-gray-800 dark:text-gray-200 group-hover:text-ios-blue transition-colors truncate">
                                {{ uploader?.displayName || 'Anonymous Contributor' }}
                              </h5>
                              <p class="text-[10px] text-gray-500 truncate">
                                @if (uploader?.username) {
                                  @{{ uploader?.username }}
                                } @else {
                                  Contributor
                                }
                              </p>
                            </div>
                            <mat-icon class="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity !text-base h-4 w-4">chevron_right</mat-icon>
                          </div>
                        } @else {
                          <p class="text-[11px] text-gray-400 italic">Anonymous Contributor</p>
                        }
                      </div>

                      <!-- Owned By Section -->
                      <div class="space-y-1.5">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">Owned By</span>
                        @if (isLoadingDetails()) {
                          <div class="flex items-center gap-2.5 animate-pulse">
                            <div class="w-8 h-8 rounded-full bg-black/10 dark:bg-white/10"></div>
                            <div class="flex-1 space-y-1">
                              <div class="h-3 w-20 bg-black/10 dark:bg-white/10 rounded"></div>
                              <div class="h-2 w-12 bg-black/10 dark:bg-white/10 rounded"></div>
                            </div>
                          </div>
                        } @else if (ownerAccountDetails()) {
                          @let owner = ownerAccountDetails();
                          <div 
                            (click)="owner ? navigateToProfile(owner.uid) : null" 
                            (keydown.enter)="owner ? navigateToProfile(owner.uid) : null"
                            tabindex="0"
                            class="flex items-center gap-2.5 cursor-pointer group hover:bg-black/5 dark:hover:bg-white/5 p-1.5 rounded-xl transition-all outline-none focus:ring-2 focus:ring-ios-blue"
                          >
                            <img 
                              [src]="owner?.photoURL || 'https://picsum.photos/seed/org/100/100'" 
                              alt="Owner profile" 
                              class="w-9 h-9 rounded-full object-cover border border-black/10 dark:border-white/10"
                              referrerpolicy="no-referrer"
                            />
                            <div class="min-w-0 flex-1">
                              <h5 class="text-xs font-bold text-gray-800 dark:text-gray-200 group-hover:text-ios-blue transition-colors truncate">
                                {{ owner?.displayName || 'Unknown Owner' }}
                              </h5>
                              <p class="text-[10px] text-gray-500 truncate">
                                @if (owner?.username) {
                                  @{{ owner?.username }}
                                } @else {
                                  Profile Owner
                                }
                              </p>
                            </div>
                            <mat-icon class="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity !text-base h-4 w-4">chevron_right</mat-icon>
                          </div>
                        } @else {
                          <p class="text-[11px] text-gray-400 italic">Unknown Owner</p>
                        }
                      </div>
                    </div>

                    <div class="border-t border-black/5 dark:border-white/5 my-3"></div>

                    <div class="space-y-2.5">
                      <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                        <span class="font-medium">Visibility</span>
                        <span class="font-bold uppercase tracking-wider text-[10px] bg-black/5 dark:bg-white/5 px-2 py-0.5 rounded">{{ selectedImg.visibility || 'public' }}</span>
                      </div>
                      <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                        <span class="font-medium">Uploaded</span>
                        <span class="text-gray-500">{{ formatDate(selectedImg.createdAt) | date:'mediumDate' }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="mt-6 pt-4 border-t border-black/5 dark:border-white/5 space-y-4">
                    <div class="flex items-center justify-between bg-black/5 dark:bg-white/5 p-4 rounded-2xl">
                      <!-- Heart Icon Action in Popup -->
                      <div class="flex items-center gap-2">
                        <button 
                          type="button"
                          (click)="toggleLike($event, selectedImg)"
                          class="flex items-center gap-1.5 cursor-pointer transition-transform active:scale-95 text-gray-700 dark:text-gray-300 bg-transparent border-none p-0"
                        >
                          <mat-icon [class.text-ios-red]="(selectedImg.likedBy || selectedImg.likes || []).includes(authService.user()?.uid || '')">
                            {{ (selectedImg.likedBy || selectedImg.likes || []).includes(authService.user()?.uid || '') ? 'favorite' : 'favorite_border' }}
                          </mat-icon>
                          <span class="text-sm font-bold">
                            {{ (selectedImg.likedBy.length || 0) | countFormat }}
                          </span>
                        </button>
                      </div>

                      <!-- Eyes metrics in popup -->
                      <div class="flex items-center gap-1.5 text-gray-500">
                        <mat-icon class="text-gray-400">visibility</mat-icon>
                        <span class="text-sm font-bold">
                          {{ (selectedImg.viewedBy.length || 0) | countFormat }}
                        </span>
                        <span class="text-[10px] uppercase font-bold tracking-widest opacity-60">views</span>
                      </div>
                    </div>

                    <div class="text-[10px] text-center text-gray-400 dark:text-gray-500 select-none">
                      Instagram Style Immersive View Frame
                    </div>
                  </div>
                </div>

              </div>
            </div>
          }
        }

        <!-- Search & Add Contact Bottom Sheet Modal -->
        @if (showAddContactModal()) {
          <div class="fixed inset-0 z-[200] flex items-end justify-center p-0 sm:p-4 animate-ios-fade">
            <div
              class="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-xl transition-opacity"
              (click)="closeAddContactModal()"
              (keydown.escape)="closeAddContactModal()"
              tabindex="-1"
              role="button"
              aria-label="Close bottom sheet backdrop"
            ></div>

            <div
              class="relative w-full max-w-xl glass-card !rounded-t-[36px] sm:!rounded-t-[40px] !rounded-b-none p-6 shadow-2xl border-t border-x border-white/30 dark:border-white/15 z-10 flex flex-col gap-4 max-h-[90vh] overflow-y-auto bg-white/85 dark:bg-zinc-900/90 backdrop-blur-3xl animate-ios-slide-up mb-0"
              (click)="$event.stopPropagation()"
            >
              <!-- Bottom Sheet Drag Handle Indicator -->
              <div class="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full mx-auto shrink-0 mb-1 opacity-80"></div>

              <div class="relative z-10 flex flex-col gap-5">
                <!-- Modal Header -->
                <div class="flex items-center justify-between border-b border-black/5 dark:border-white/10 pb-4">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                      <mat-icon>person_search</mat-icon>
                    </div>
                    <div>
                      <h3 class="text-base font-bold text-zinc-900 dark:text-white leading-tight">Find Account by Phone</h3>
                      <p class="text-[11px] text-zinc-500 dark:text-zinc-400">Enter a 10-digit phone number for auto-search</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    (click)="closeAddContactModal()"
                    class="w-8 h-8 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 text-zinc-500 hover:text-zinc-900 dark:hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <mat-icon class="!text-[18px]">close</mat-icon>
                  </button>
                </div>

                <!-- Phone Search Bar -->
                <div class="space-y-2">
                  <label class="text-[11px] font-black uppercase tracking-wider text-zinc-500 dark:text-zinc-400 block px-1">
                    10-Digit Phone Number
                  </label>
                  <div class="flex items-center gap-2">
                    <div class="flex-1 ios-input-container !rounded-2xl !h-12 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-3 flex items-center gap-2">
                      <mat-icon class="text-zinc-400 !text-[20px]">phone</mat-icon>
                      <input
                        type="tel"
                        [value]="searchContactPhone"
                        (input)="onContactPhoneInput($any($event.target).value)"
                        placeholder="e.g. 9876543210"
                        maxlength="14"
                        class="w-full bg-transparent border-none outline-none text-sm font-bold text-zinc-900 dark:text-white placeholder:text-zinc-400"
                      />
                      @if (searchContactPhone) {
                        <button
                          type="button"
                          (click)="searchContactPhone = ''; searchContactPerformed.set(false); matchedContactAccount.set(null)"
                          class="text-zinc-400 hover:text-zinc-600 cursor-pointer"
                        >
                          <mat-icon class="!text-[16px]">close</mat-icon>
                        </button>
                      }
                    </div>

                    @if (isSearchingContact()) {
                      <div class="w-10 h-10 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                        <div class="w-5 h-5 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
                      </div>
                    }
                  </div>
                </div>

                <!-- Result Box -->
                @if (searchContactPerformed()) {
                  <div class="mt-2 space-y-4 animate-ios-slide">
                    @if (matchedContactAccount(); as matched) {
                      <div class="space-y-3 bg-black/5 dark:bg-white/5 p-4 rounded-3xl border border-black/5 dark:border-white/10">
                        <div class="flex items-center justify-between">
                          <span class="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <mat-icon class="!text-[14px]">check_circle</mat-icon> Account Match Found
                          </span>
                          <span class="text-[10px] font-bold text-zinc-500 uppercase">
                            {{ matched.accountType === 'organization' ? 'Organization' : 'Personal' }}
                          </span>
                        </div>

                        <div class="flex items-center gap-3">
                          <div class="w-10 h-10 rounded-full overflow-hidden bg-ios-blue/10 shrink-0">
                            @if (matched.photoURL) {
                              <img [src]="matched.photoURL" [alt]="matched.displayName" class="w-full h-full object-cover" />
                            } @else {
                              <div class="w-full h-full flex items-center justify-center text-ios-blue font-bold">
                                {{ (matched.displayName || 'U').charAt(0).toUpperCase() }}
                              </div>
                            }
                          </div>
                          <div class="min-w-0 flex-1">
                            <h4 class="text-sm font-bold text-zinc-900 dark:text-white truncate">{{ matched.displayName }}</h4>
                            <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate">{{ matched.username ? '@' + matched.username : matched.email }}</p>
                          </div>
                        </div>

                        <!-- Category Selector for adding -->
                        <div class="space-y-1.5 pt-2">
                          <label class="text-[10px] font-black uppercase tracking-wider text-zinc-500 dark:text-zinc-400 block">
                            Assign Contact Category
                          </label>
                          <div class="flex flex-wrap gap-2">
                            @if (matched.accountType === 'organization') {
                              <span class="px-3 py-1 rounded-full text-xs font-bold bg-ios-blue text-white border border-ios-blue">
                                Organisation
                              </span>
                            } @else {
                              @for (cat of ['Friends', 'Family']; track cat) {
                                <button
                                  type="button"
                                  (click)="searchContactCategory.set(cat)"
                                  class="px-3 py-1 rounded-full text-xs font-bold border transition-all cursor-pointer"
                                  [class.bg-ios-blue]="searchContactCategory() === cat"
                                  [class.text-white]="searchContactCategory() === cat"
                                  [class.border-ios-blue]="searchContactCategory() === cat"
                                  [class.bg-black/5]="searchContactCategory() !== cat"
                                  [class.dark:bg-white/5]="searchContactCategory() !== cat"
                                  [class.text-zinc-600]="searchContactCategory() !== cat"
                                  [class.dark:text-zinc-400]="searchContactCategory() !== cat"
                                  [class.border-transparent]="searchContactCategory() !== cat"
                                >
                                  {{ cat }}
                                </button>
                              }
                            }
                          </div>
                        </div>

                        <!-- Add Button -->
                        <div class="pt-2">
                          @if (isAlreadyUserContact(matched.uid)) {
                            <div class="w-full py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold text-center flex items-center justify-center gap-1.5">
                              <mat-icon class="!text-[18px]">verified</mat-icon>
                              <span>Already in your contacts list</span>
                            </div>
                          } @else {
                            <button
                              type="button"
                              (click)="addContactFromModal(matched)"
                              [disabled]="isAddingContact()"
                              class="w-full py-3.5 rounded-2xl bg-ios-blue text-white font-bold text-xs shadow-lg shadow-ios-blue/20 hover:scale-[1.01] active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                            >
                              @if (isAddingContact()) {
                                <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>Saving Contact...</span>
                              } @else {
                                <mat-icon class="!text-[18px]">person_add</mat-icon>
                                <span>Add to Contacts ({{ searchContactCategory() }})</span>
                              }
                            </button>
                          }
                        </div>
                      </div>
                    } @else {
                      <!-- No Match Found -->
                      <div class="p-6 text-center bg-ios-red/5 rounded-3xl border border-ios-red/10 text-ios-red space-y-2">
                        <mat-icon class="!text-[32px] opacity-80">no_accounts</mat-icon>
                        <h4 class="text-sm font-bold">No Account Found</h4>
                        <p class="text-xs opacity-80 leading-relaxed max-w-xs mx-auto">
                          No registered user or organization was found with phone number <strong>{{ searchContactPhone }}</strong>.
                        </p>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    }
  `,
})
export class SearchView implements OnInit, OnDestroy {
  public router = inject(Router);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  authService = inject(AuthService);
  searchService = inject(SearchService);
  searchDataService = inject(SearchDataService);
  chatService = inject(ChatService);
  themeService = inject(ThemeService);
  notificationService = inject(NotificationService);
  private sanitizer = inject(DomSanitizer);

  query = signal("");
  category = signal("all");
  chatsSubcategory = signal<'person' | 'organization' | 'contact'>('person');
  chatFilterChips = [
    { id: 'person' as const, label: 'Person', icon: 'person' },
    { id: 'organization' as const, label: 'Organization', icon: 'corporate_fare' },
    { id: 'contact' as const, label: 'Contact', icon: 'contacts' },
  ];
  userContacts = signal<ContactItem[]>([]);
  private unsubscribeContacts: Unsubscribe | null = null;
  showAddContactModal = signal<boolean>(false);
  searchContactPhone = '';
  isSearchingContact = signal<boolean>(false);
  matchedContactAccount = signal<AuthProfile | null>(null);
  searchContactCategory = signal<string>('Friends');
  searchContactPerformed = signal<boolean>(false);
  isAddingContact = signal<boolean>(false);
  hasSearched = signal(false);
  isScrolled = signal(false);
  webViewUrl = signal<string | null>(null);
  webViewLoading = signal(false);
  isListening = signal(false);
  isMounted = signal(true);

  jobs = signal<JobModel[]>([]);
  products = signal<ProductModel[]>([]);
  videos = signal<VideoModel[]>([]);

  isLoading = computed(() => this.searchService.loading() || this.searchDataService.loading());
  private recognition: any = null;

  formatDate(ts: unknown): Date | null {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    const timestamp = ts as { toDate?: () => Date };
    if (typeof timestamp.toDate === "function") return timestamp.toDate();
    if (typeof ts === "string" || typeof ts === "number") return new Date(ts);
    return null;
  }

  safeWebViewUrl = computed(() => {
    const url = this.webViewUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  selectedImage = signal<UserImage | null>(null);
  isImagePopupOpen = signal<boolean>(false);
  uploaderAccountDetails = signal<AuthProfile | null>(null);
  ownerAccountDetails = signal<AuthProfile | null>(null);
  isLoadingDetails = signal<boolean>(false);

  async toggleLike(event: Event, img: UserImage) {
    event.stopPropagation();
    const currentUser = this.authService.user();
    if (!currentUser) {
      this.notificationService.error("Please log in to like images.");
      return;
    }

    const userId = currentUser.uid;
    const ownerUid = img.ownerUid || (img as unknown as { ownerId?: string }).ownerId;
    if (!img.id || !ownerUid) {
      console.error("Missing image ID or ownerUid", img);
      return;
    }

    const currentLikedBy = img.likedBy || img.likes || [];
    const isLiked = currentLikedBy.includes(userId);
    const newLikedBy = isLiked
      ? currentLikedBy.filter(id => id !== userId)
      : [...currentLikedBy, userId];

    // Optimistically update local model
    img.likedBy = newLikedBy;

    try {
      const docRef = doc(db, `accounts/${ownerUid}/images`, img.id);
      await updateDoc(docRef, {
        likedBy: isLiked ? arrayRemove(userId) : arrayUnion(userId)
      });
    } catch (err) {
      console.error("Error updating like status:", err);
      // Rollback local model
      img.likedBy = currentLikedBy;
      this.notificationService.error("Failed to update like status");
    }
  }

  async openImagePopup(img: UserImage) {
    const ownerUid = img.ownerUid || (img as unknown as { ownerId?: string }).ownerId;
    if (ownerUid && img.id) {
      this.router.navigate(['/image', ownerUid, img.id]);
    } else if (img.id) {
      this.router.navigate(['/image', img.id]);
    } else {
      console.warn("Could not navigate: image missing ID details:", img);
    }
  }

  closeImagePopup() {
    this.isImagePopupOpen.set(false);
    this.selectedImage.set(null);
  }

  navigateToProfile(uid: string) {
    this.closeImagePopup();
    this.router.navigate(["/profile", uid]);
  }

  async deleteChatFromSearch(event: Event, chat: any) {
    event.stopPropagation();
    event.preventDefault();
    if (!chat || !chat.id) return;
    if (confirm(`Are you sure you want to delete chat with ${chat.participantName || 'this contact'}?`)) {
      try {
        await this.chatService.deleteChat(chat.id);
        this.notificationService.success('Chat deleted successfully');
      } catch (err) {
        console.error('Error deleting chat:', err);
      }
    }
  }

  async openChatResult(chat: any) {
    if (!chat) return;
    const participantId = chat.participantId || chat.uid;
    const participantName = chat.participantName || chat.displayName || 'User';
    const participantPhoto = chat.participantPhoto || chat.photoURL || '';
    let chatId = chat.id;

    if (!chatId && participantId) {
      try {
        chatId = await this.chatService.getOrCreateChat(
          participantId,
          participantName,
          participantPhoto
        );
      } catch (err) {
        console.error('Error getting/creating chat in search:', err);
      }
    }

    if (chatId) {
      this.router.navigate(['/chat', chatId], {
        queryParams: {
          uid: participantId || '',
          name: participantName || '',
          photo: participantPhoto || ''
        }
      });
    }
  }

  onBackdropClick(event: Event) {
    if (event.target === event.currentTarget) {
      this.closeImagePopup();
    }
  }

  // Swipeable Category Chips featuring All, Search, Productivity, and System
  hubCategories = [
    { id: 'all' as const, label: 'All', icon: 'apps' },
    { id: 'search' as const, label: 'Search', icon: 'search' },
    { id: 'productivity' as const, label: 'Productivity', icon: 'workplace' },
    { id: 'system' as const, label: 'System', icon: 'settings' },
  ];

  activeHubCategory = signal<'all' | 'search' | 'productivity' | 'system'>('all');

  chipScrollContainer = viewChild<ElementRef<HTMLElement>>('chipScrollContainer');
  headerChipScrollContainer = viewChild<ElementRef<HTMLElement>>('headerChipScrollContainer');

  private isChipDragging = false;
  private chipStartX = 0;
  private chipScrollLeft = 0;

  startChipDrag(e: MouseEvent, targetEl?: HTMLElement | null) {
    const el = targetEl || this.chipScrollContainer()?.nativeElement;
    if (!el) return;
    this.isChipDragging = true;
    this.chipStartX = e.pageX - el.offsetLeft;
    this.chipScrollLeft = el.scrollLeft;
  }

  stopChipDrag() {
    this.isChipDragging = false;
  }

  onChipDrag(e: MouseEvent, targetEl?: HTMLElement | null) {
    if (!this.isChipDragging) return;
    const el = targetEl || this.chipScrollContainer()?.nativeElement;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - this.chipStartX) * 1.5;
    el.scrollLeft = this.chipScrollLeft - walk;
  }

  private chipTouchStartX = 0;
  private chipTouchStartY = 0;

  onChipTouchStart(event: TouchEvent) {
    if (event.touches.length > 1) return;
    this.chipTouchStartX = event.changedTouches[0].screenX;
    this.chipTouchStartY = event.changedTouches[0].screenY;
  }

  onChipTouchEnd(event: TouchEvent) {
    if (event.changedTouches.length === 0) return;
    const diffX = this.chipTouchStartX - event.changedTouches[0].screenX;
    const diffY = this.chipTouchStartY - event.changedTouches[0].screenY;
    const swipeThreshold = 45;

    // Strict horizontal check: only switch if horizontal swipe exceeds vertical gesture by 1.8x
    // Never calls preventDefault so vertical scrolling is 100% smooth and continuous
    if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > Math.abs(diffY) * 1.8) {
      const hubList: ('all' | 'search' | 'productivity' | 'system')[] = ['all', 'search', 'productivity', 'system'];
      const currentIndex = hubList.indexOf(this.activeHubCategory());
      if (diffX > 0 && currentIndex < hubList.length - 1) {
        this.onHubCategorySelect(hubList[currentIndex + 1]);
      } else if (diffX < 0 && currentIndex > 0) {
        this.onHubCategorySelect(hubList[currentIndex - 1]);
      }
    }
  }

  onHubCategorySelect(catId: 'all' | 'search' | 'productivity' | 'system') {
    this.activeHubCategory.set(catId);
    if (this.isBrowser) {
      setTimeout(() => {
        const chip = document.getElementById('hub-chip-' + catId) || document.getElementById('header-hub-chip-' + catId);
        chip?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }, 50);
    }
    if (this.hasSearched()) {
      if (catId === 'all') {
        this.onCategorySelect('All');
      } else if (catId === 'search') {
        this.onCategorySelect('Web');
      } else if (catId === 'productivity') {
        this.onCategorySelect('Jobs');
      } else if (catId === 'system') {
        this.onCategorySelect('Organization');
      }
    }
  }

  searchCategories = [
    { name: "All", icon: "search" },
    { name: "Web", icon: "language" },
    { name: "Chats", icon: "chat" },
    { name: "Person", icon: "person" },
    { name: "Organization", icon: "corporate_fare" },
    { name: "Country", icon: "country" },
    { name: "News", icon: "newspaper" },
    { name: "Images", icon: "image" },
    { name: "Voice", icon: "mic" },
    { name: "Jobs", icon: "work" },
    { name: "Products", icon: "shopping_bag" },
    { name: "Videos", icon: "videocam" },
    { name: "Music", icon: "music_note" },
    { name: "Radio", icon: "radio" },
    { name: "Channel", icon: "channel" },
  ];

  isStaffUser = signal<boolean>(false);

  async checkStaffAccess() {
    const user = this.authService.user();
    if (!user) {
      this.isStaffUser.set(false);
      return;
    }

    const email = user.email ? user.email.toLowerCase().trim() : '';
    if (
      email === MASTER_ADMIN_EMAIL.toLowerCase() ||
      email === "kic9le@gmail.com" ||
      email === "kicgledotcom@gmail.com" ||
      this.authService.isAdmin()
    ) {
      this.isStaffUser.set(true);
      return;
    }

    try {
      // 1. Check in settings/authorized_person
      const docRef = doc(db, "settings", "authorized_person");
      try {
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          const rawStaff = data ? data["staff"] : null;
          let staffList: any[] = [];
          if (Array.isArray(rawStaff)) {
            staffList = rawStaff;
          } else if (rawStaff && typeof rawStaff === 'object') {
            staffList = Object.entries(rawStaff).map(([key, val]: [string, any]) => {
              if (typeof val === 'object' && val !== null) {
                return { uid: val.uid || key, ...val };
              }
              return { uid: key, roles: val };
            });
          }

          const found = staffList.find((s: any) => s && (
            s.uid === user.uid ||
            (email && s.email && s.email.toLowerCase().trim() === email)
          ));

          if (found) {
            if (Array.isArray(found.roles) && found.roles.length > 0) {
              this.isStaffUser.set(true);
              return;
            }
            if (found.roles && typeof found.roles === 'object' && Object.values(found.roles).some((v) => v === true)) {
              this.isStaffUser.set(true);
              return;
            }
            if (found.isFounder || found.role || found.isStaff) {
              this.isStaffUser.set(true);
              return;
            }
          }
        }
      } catch (settingsErr) {
        // Safe fallback if settings doc is missing or network/auth state is pending
      }

      // 2. Fallback check in authorized_admins
      try {
        const adminSnap = await getDoc(doc(db, "authorized_admins", user.uid));
        if (adminSnap.exists()) {
          const roles = adminSnap.data()["roles"] || {};
          if (Object.values(roles).some((v) => v === true)) {
            this.isStaffUser.set(true);
            return;
          }
        }
      } catch (adminErr) {
        // Safe fallback if admin doc does not exist
      }

      this.isStaffUser.set(false);
    } catch (err) {
      this.isStaffUser.set(false);
    }
  }

  baseShortcuts: Shortcut[] = [
    {
      label: "Profile",
      icon: "account_circle",
      path: "/profile",
      queryParams: {},
    },
    {
      label: "Web",
      icon: "language",
      path: "/search",
      queryParams: { q: "", c: "web" },
    },
    {
      label: "Contacts",
      icon: "contacts",
      path: "/contacts",
      queryParams: {},
    },
    {
      label: "Chats",
      icon: "chat",
      path: "/search",
      queryParams: { q: "", c: "chats" },
    },
    {
      label: "Person",
      icon: "person",
      path: "/search",
      queryParams: { q: "", c: "person" },
    },
    {
      label: "Organization",
      icon: "corporate_fare",
      path: "/search",
      queryParams: { q: "", c: "organization" },
    },
    {
      label: "Country",
      icon: "country",
      path: "/search",
      queryParams: { q: "", c: "country" },
    },
    {
      label: "News",
      icon: "newspaper",
      path: "/search",
      queryParams: { q: "", c: "news" },
    },
    {
      label: "Images",
      icon: "image",
      path: "/search",
      queryParams: { q: "", c: "images" },
    },
    {
      label: "Voice",
      icon: "mic",
      path: "/search",
      queryParams: { q: "", c: "voice" },
    },
    {
      label: "Jobs",
      icon: "work",
      path: "/search",
      queryParams: { q: "", c: "jobs" },
    },
    {
      label: "Products",
      icon: "shopping_bag",
      path: "/search",
      queryParams: { q: "", c: "products" },
    },
    {
      label: "Videos",
      icon: "videocam",
      path: "/search",
      queryParams: { q: "", c: "videos" },
    },
    {
      label: "Music",
      icon: "music_note",
      path: "/search",
      queryParams: { q: "", c: "music" },
    },
    {
      label: "Radio",
      icon: "radio",
      path: "/search",
      queryParams: { q: "", c: "radio" },
    },
    {
      label: "Channel",
      icon: "channel",
      path: "/search",
      queryParams: { q: "", c: "channel" },
    },
    {
      label: "Workplace",
      icon: "workplace",
      path: "/workplace",
      queryParams: {},
    },
    {
      label: "Planner",
      icon: "planner",
      path: "/planner",
      queryParams: {},
    },
    {
      label: "Ads",
      icon: "ads",
      path: "/ads",
      queryParams: {},
    },
    {
      label: "Education",
      icon: "education_icon",
      path: "/education",
      queryParams: {},
    },
    {
      label: "Games",
      icon: "games_icon",
      path: "/games",
      queryParams: {},
    },
    {
      label: "Services",
      icon: "services_icon",
      path: "/services",
      queryParams: {},
    },
    {
      label: "Lifeline",
      icon: "lifeline",
      path: "/lifeline",
      queryParams: {},
    },
  ];

  shortcutToggles = signal<Record<string, boolean>>({});
  private unsubscribeShortcuts: Unsubscribe | null = null;

  displayedShortcuts = computed<Shortcut[]>(() => {
    const list: Shortcut[] = [...this.baseShortcuts];

    if (this.isStaffUser()) {
      list.push({
        label: "Admin",
        icon: "admin_panel_settings",
        path: "/admin",
        queryParams: {},
      });
    }

    list.push(
      {
        label: "Settings",
        icon: "settings",
        path: "/settings",
        queryParams: {},
      },
      {
        label: "Ever",
        icon: "ever",
        path: "/ever",
        queryParams: {},
      },
      {
        label: "Policies",
        icon: "policy",
        path: "/policies",
        queryParams: {},
      },
      {
        label: "About",
        icon: "info",
        path: "/about",
        queryParams: {},
      }
    );

    const toggles = this.shortcutToggles();
    const enabledList = list.filter(s => {
      const id = s.label.toLowerCase();
      if (toggles[id] === false || toggles[s.label] === false) {
        return false;
      }
      return true;
    });

    const hub = this.activeHubCategory();
    let filtered = enabledList;
    if (hub === 'search') {
      const searchLabels = new Set([
        'Profile', 'Web', 'Contacts', 'Chats', 'Person', 'Organization',
        'Country', 'News', 'Images', 'Voice', 'Jobs', 'Products',
        'Videos', 'Music', 'Radio', 'Channel'
      ]);
      filtered = enabledList.filter(s => searchLabels.has(s.label));
    } else if (hub === 'productivity') {
      const productivityLabels = new Set([
        'Workplace', 'Planner', 'Ads', 'Services', 'Lifeline', 'Education', 'Games'
      ]);
      filtered = enabledList.filter(s => productivityLabels.has(s.label));
    } else if (hub === 'system') {
      const systemLabels = new Set([
        'Admin', 'Settings', 'Ever', 'Policies', 'About'
      ]);
      filtered = enabledList.filter(s => systemLabels.has(s.label));
    }

    // Always structure the shortcuts list so Ever, Policies, and About are at the very end
    const endLabels = ['Ever', 'Policies', 'About'];
    const mainList = filtered.filter(s => !endLabels.includes(s.label));
    const endList = endLabels
      .map(lbl => filtered.find(s => s.label === lbl))
      .filter((s): s is Shortcut => !!s);

    return [...mainList, ...endList];
  });

  shortcuts = computed(() => this.displayedShortcuts());

  resultsContainer = viewChild<ElementRef>("resultsContainer");

  hasResultsForCurrentCategory = computed(() => {
    const res = this.searchService.response();
    const cat = this.category();

    if (cat === "all") {
      return (
        this.persons().length > 0 ||
        this.orgs().length > 0 ||
        this.countries().length > 0 ||
        (res && res.sources && res.sources.length > 0) ||
        (res && res.chats && res.chats.length > 0) ||
        this.filteredContacts().length > 0 ||
        (res && res.userImages && res.userImages.length > 0) ||
        (res && res.news && res.news.length > 0) ||
        (this.jobs() && this.jobs().length > 0) ||
        (this.products() && this.products().length > 0) ||
        (this.videos() && this.videos().length > 0)
      );
    }

    if (cat === "web") return !!(res && res.sources && res.sources.length > 0);
    if (cat === "chats") return !!(res && res.chats && res.chats.length > 0) || this.filteredContacts().length > 0;
    if (cat === "person") return this.persons().length > 0;
    if (cat === "organization") return this.orgs().length > 0;
    if (cat === "country") return this.countries().length > 0;

    if (cat === "images") {
      return !!(res && res.userImages && res.userImages.length > 0);
    }

    if (cat === "news") return !!(res && res.news && res.news.length > 0);

    if (cat === "jobs") return this.jobs() && this.jobs().length > 0;
    if (cat === "products") return this.products() && this.products().length > 0;
    if (cat === "videos") return this.videos() && this.videos().length > 0;
    if (cat === "voice") return false;
    if (cat === "music") return false;

    return false;
  });

  getShortcutBadgeCount(_label: string): number | null {
    return null;
  }

  // IP-based Geo Location for Pinned Country
  userGeoCountry = signal<{ code?: string; name?: string } | null>(null);

  // Paginated Infinite Scroll State for Person and Organization
  personAccounts = signal<AuthProfile[]>([]);
  hasMorePersons = signal<boolean>(true);
  isLoadingMorePersons = signal<boolean>(false);
  private lastPersonDocSnap: any = null;

  orgAccounts = signal<AuthProfile[]>([]);
  hasMoreOrgs = signal<boolean>(true);
  isLoadingMoreOrgs = signal<boolean>(false);
  private lastOrgDocSnap: any = null;

  async detectUserGeoCountry() {
    if (this.userGeoCountry()) return;
    try {
      const res = await fetch('https://ipapi.co/json/').then(r => r.json());
      if (res && (res.country_name || res.country_code)) {
        this.userGeoCountry.set({ code: res.country_code, name: res.country_name || res.country });
        return;
      }
    } catch {
      try {
        const res2 = await fetch('https://ip-api.com/json').then(r => r.json());
        if (res2 && (res2.country || res2.countryCode)) {
          this.userGeoCountry.set({ code: res2.countryCode, name: res2.country });
          return;
        }
      } catch {
        console.warn('Geo IP lookup fallback used');
      }
    }
  }

  async loadMorePersons() {
    if (this.isLoadingMorePersons() || !this.hasMorePersons()) return;
    this.isLoadingMorePersons.set(true);
    try {
      const accountsRef = collection(db, 'accounts');
      let q;
      if (this.lastPersonDocSnap) {
        q = query(
          accountsRef,
          where('accountType', '==', 'person'),
          where('status', '==', 'active'),
          startAfter(this.lastPersonDocSnap),
          limit(8)
        );
      } else {
        q = query(
          accountsRef,
          where('accountType', '==', 'person'),
          where('status', '==', 'active'),
          limit(8)
        );
      }
      const snap = await getDocs(q);
      if (!snap.empty) {
        this.lastPersonDocSnap = snap.docs[snap.docs.length - 1];
        const newItems = snap.docs.map(doc => ({ ...doc.data(), uid: doc.id }) as AuthProfile);

        this.personAccounts.update(existing => {
          const uids = new Set(existing.map(a => a.uid));
          const append = newItems.filter(a => a.uid && !uids.has(a.uid));
          return [...existing, ...append];
        });
        this.hasMorePersons.set(snap.docs.length >= 8);
      } else {
        this.hasMorePersons.set(false);
      }
    } catch (err) {
      console.error('Error loading more persons:', err);
    } finally {
      this.isLoadingMorePersons.set(false);
    }
  }

  async loadMoreOrgs() {
    if (this.isLoadingMoreOrgs() || !this.hasMoreOrgs()) return;
    this.isLoadingMoreOrgs.set(true);
    try {
      const accountsRef = collection(db, 'accounts');
      let q;
      if (this.lastOrgDocSnap) {
        q = query(
          accountsRef,
          where('accountType', '==', 'organization'),
          where('status', '==', 'active'),
          startAfter(this.lastOrgDocSnap),
          limit(8)
        );
      } else {
        q = query(
          accountsRef,
          where('accountType', '==', 'organization'),
          where('status', '==', 'active'),
          limit(8)
        );
      }
      const snap = await getDocs(q);
      if (!snap.empty) {
        this.lastOrgDocSnap = snap.docs[snap.docs.length - 1];
        const newItems = snap.docs.map(doc => ({ ...doc.data(), uid: doc.id }) as AuthProfile);

        this.orgAccounts.update(existing => {
          const uids = new Set(existing.map(a => a.uid));
          const append = newItems.filter(a => a.uid && !uids.has(a.uid));
          return [...existing, ...append];
        });
        this.hasMoreOrgs.set(snap.docs.length >= 8);
      } else {
        this.hasMoreOrgs.set(false);
      }
    } catch (err) {
      console.error('Error loading more orgs:', err);
    } finally {
      this.isLoadingMoreOrgs.set(false);
    }
  }

  persons = computed(() => {
    const res = this.searchService.response();
    const base = res?.accounts?.filter((a) => a.accountType !== "organization" && a.accountType !== "country") || [];
    const paginated = this.personAccounts();
    const map = new Map<string, AuthProfile>();
    base.forEach(a => map.set(a.uid, a));
    paginated.forEach(a => map.set(a.uid, a));
    const list = Array.from(map.values());

    const qText = this.query().toLowerCase().trim();
    if (qText) {
      return list.filter(acc => (acc.displayName && acc.displayName.toLowerCase().includes(qText)) || (acc.username && acc.username.toLowerCase().includes(qText)));
    }
    return list;
  });

  orgs = computed(() => {
    const res = this.searchService.response();
    const base = res?.accounts?.filter((a) => a.accountType === "organization") || [];
    const paginated = this.orgAccounts();
    const map = new Map<string, AuthProfile>();
    base.forEach(a => map.set(a.uid, a));
    paginated.forEach(a => map.set(a.uid, a));
    const list = Array.from(map.values());

    const qText = this.query().toLowerCase().trim();
    if (qText) {
      return list.filter(acc => (acc.displayName && acc.displayName.toLowerCase().includes(qText)) || (acc.username && acc.username.toLowerCase().includes(qText)));
    }
    return list;
  });

  countries = computed(() => {
    const res = this.searchService.response();
    let list: AuthProfile[] = [];
    if (res && res.accounts) {
      list = res.accounts.filter((a) => a.accountType === "country" || (a as any).account_type === "country");
    }

    // Sort alphabetically by displayName or name
    list.sort((a, b) => {
      const nameA = (a.displayName || a.name || '').toLowerCase();
      const nameB = (b.displayName || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const geo = this.userGeoCountry();
    if (geo) {
      const geoName = (geo.name || '').toLowerCase().trim();
      const geoCode = (geo.code || '').toLowerCase().trim();

      const pinnedIdx = list.findIndex(acc => {
        const accName = (acc.displayName || acc.name || '').toLowerCase().trim();
        const accCode = (acc.countryCode || acc.documentNumber || '').toLowerCase().trim();
        return (geoName && (accName.includes(geoName) || geoName.includes(accName))) ||
               (geoCode && accCode === geoCode);
      });

      if (pinnedIdx > -1) {
        const pinned = { ...list[pinnedIdx], isPinnedUserCountry: true };
        const remaining = list.filter((_, idx) => idx !== pinnedIdx);
        return [pinned, ...remaining];
      }
    }

    return list;
  });

  chatAccountTypes = signal<Record<string, string>>({});
  private fetchedChatAccountUids = new Set<string>();

  getChatAccountType(chat: any): string {
    if (!chat) return '';
    const participantId = chat.participantId || chat.uid;
    if (participantId) {
      const lookedUpType = this.chatAccountTypes()[participantId];
      if (lookedUpType !== undefined) {
        return lookedUpType;
      }
    }
    return (chat.participantAccountType || '').toString().trim();
  }

  isParticipantPerson(chat: any): boolean {
    const accountType = this.getChatAccountType(chat);
    return accountType === 'Person' || accountType.toLowerCase() === 'person';
  }

  isParticipantOrg(chat: any): boolean {
    const accountType = this.getChatAccountType(chat);
    return accountType === 'Organization' || accountType.toLowerCase() === 'organization';
  }

  displayedChats = computed(() => {
    const sub = this.chatsSubcategory();
    const chats = (this.searchService.response()?.chats || []).filter(
      c => c && c.lastMessage && c.lastMessage !== 'No messages yet'
    );
    if (sub === "person") {
      return chats.filter(c => this.isParticipantPerson(c));
    }
    if (sub === "organization") {
      return chats.filter(c => this.isParticipantOrg(c));
    }
    return chats;
  });

  filteredContacts = computed(() => {
    const list = this.userContacts();
    const q = this.query().trim().toLowerCase();
    const currentUser = this.authService.userProfile();

    let contacts = [...list];
    const myContactIndex = contacts.findIndex(c => this.isMeContact(c));
    let myContact: ContactItem | null = null;

    if (myContactIndex >= 0) {
      myContact = contacts.splice(myContactIndex, 1)[0];
    } else if (currentUser) {
      myContact = {
        id: "me-" + currentUser.uid,
        uid: currentUser.uid,
        displayName: (currentUser.displayName || "You") + " (You)",
        username: currentUser.username || "",
        phoneNumber: currentUser.phoneNumber || "",
        photoURL: currentUser.photoURL || "",
        email: currentUser.email || "",
        accountType: currentUser.accountType || "person",
        category: "Me",
        bio: currentUser.bio || "Note to self",
        isOnline: true
      };
    }

    if (q) {
      contacts = contacts.filter(c =>
        (c.displayName && c.displayName.toLowerCase().includes(q)) ||
        (c.username && c.username.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phoneNumber && c.phoneNumber.includes(q)) ||
        (c.category && c.category.toLowerCase().includes(q))
      );
    }

    if (myContact && (!q || myContact.displayName.toLowerCase().includes(q))) {
      contacts.unshift(myContact);
    }

    return contacts;
  });

  isMeContact(contact: ContactItem): boolean {
    const user = this.authService.user();
    return !!user && (contact.uid === user.uid);
  }

  getContactCategoryIcon(cat: string): string {
    switch (cat) {
      case "All": return "apps";
      case "Organisation": return "business";
      case "Family": return "family_restroom";
      case "Friends": return "people";
      default: return "folder";
    }
  }

  async deleteContactFromSearch(contact: ContactItem) {
    const user = this.authService.user();
    if (!user || !contact.id || this.isMeContact(contact)) return;
    if (confirm(`Do you want to delete ${contact.displayName} from your contacts?`)) {
      try {
        await deleteDoc(doc(db, `accounts/${user.uid}/contacts`, contact.id));
        this.notificationService.success("Contact removed");
      } catch (err) {
        console.error("Error deleting contact:", err);
      }
    }
  }

  async startChatWithContact(contact: ContactItem) {
    await this.openChatResult({
      participantId: contact.uid,
      participantName: contact.displayName,
      participantPhoto: contact.photoURL
    });
  }

  openAddContactModal() {
    this.searchContactPhone = '';
    this.matchedContactAccount.set(null);
    this.searchContactPerformed.set(false);
    this.showAddContactModal.set(true);
  }

  closeAddContactModal() {
    this.showAddContactModal.set(false);
  }

  cleanDigits(phone: string): string {
    return phone ? phone.replace(/\D/g, '') : '';
  }

  formatPhoneNumber(phone?: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    return phone;
  }

  onContactPhoneInput(value: string) {
    this.searchContactPhone = value;
    const digits = this.cleanDigits(value);
    if (digits.length === 10) {
      this.onContactPhoneSearch(digits);
    } else if (digits.length < 10) {
      this.searchContactPerformed.set(false);
      this.matchedContactAccount.set(null);
    }
  }

  async onContactPhoneSearch(rawDigits: string) {
    if (!rawDigits || rawDigits.length !== 10) return;
    this.isSearchingContact.set(true);
    this.searchContactPerformed.set(false);
    this.matchedContactAccount.set(null);

    try {
      const accountsRef = collection(db, 'accounts');
      let snap = await getDocs(query(accountsRef, where('phoneNumber', '==', rawDigits)));
      if (snap.empty) {
        const formatted = this.formatPhoneNumber(rawDigits);
        snap = await getDocs(query(accountsRef, where('phoneNumber', '==', formatted)));
      }
      if (snap.empty) {
        snap = await getDocs(query(accountsRef, where('phone', '==', rawDigits)));
      }

      if (!snap.empty) {
        const firstDoc = snap.docs[0];
        const data = firstDoc.data() as Record<string, any>;
        const isOrg = data['accountType'] === 'organization' || Boolean(data['isOrganization']);
        const accountType = (isOrg ? 'organization' : 'person') as 'person' | 'organization';

        const account = {
          ...data,
          uid: firstDoc.id,
          displayName: data['displayName'] || 'User',
          photoURL: data['photoURL'] || '',
          email: data['email'] || '',
          accountType: accountType
        } as AuthProfile;

        if (accountType === 'organization') {
          this.searchContactCategory.set('Organisation');
        } else {
          this.searchContactCategory.set('Friends');
        }

        this.matchedContactAccount.set(account);
      } else {
        this.matchedContactAccount.set(null);
      }
    } catch (error) {
      console.error('Error searching accounts by phone:', error);
    } finally {
      this.isSearchingContact.set(false);
      this.searchContactPerformed.set(true);
    }
  }

  isAlreadyUserContact(uid: string): boolean {
    return this.userContacts().some(c => c.uid === uid);
  }

  async addContactFromModal(account: AuthProfile) {
    const currentUser = this.authService.user();
    if (!currentUser?.uid || !account.uid) {
      this.notificationService.error('Please sign in to manage contacts.');
      return;
    }

    this.isAddingContact.set(true);
    try {
      const contactDocRef = doc(db, 'accounts', currentUser.uid, 'contacts', account.uid);
      await setDoc(contactDocRef, {
        uid: account.uid,
        displayName: account.displayName || 'User',
        username: account.username || '',
        phoneNumber: account.phoneNumber || this.searchContactPhone || '',
        photoURL: account.photoURL || '',
        email: account.email || '',
        accountType: account.accountType || 'person',
        category: this.searchContactCategory() || 'Friends',
        bio: account.bio || '',
        createdAt: serverTimestamp()
      });

      this.notificationService.success(`Added ${account.displayName} to ${this.searchContactCategory()} contacts!`);
      this.closeAddContactModal();
    } catch (error) {
      console.error('Error adding contact:', error);
      this.notificationService.error('Failed to add contact.');
    } finally {
      this.isAddingContact.set(false);
    }
  }

  loadUserContacts() {
    if (this.unsubscribeContacts) {
      this.unsubscribeContacts();
      this.unsubscribeContacts = null;
    }
    const user = this.authService.user();
    if (!user?.uid) {
      this.userContacts.set([]);
      return;
    }
    const contactsPath = `accounts/${user.uid}/contacts`;
    this.unsubscribeContacts = onSnapshot(collection(db, contactsPath), (snapshot) => {
      const list: ContactItem[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          uid: data["uid"] || docSnap.id,
          displayName: data["displayName"] || "User",
          username: data["username"] || "",
          phoneNumber: data["phoneNumber"] || "",
          photoURL: data["photoURL"] || "",
          email: data["email"] || "",
          accountType: data["accountType"] || "user",
          category: data["category"] || "Friends",
          bio: data["bio"] || "",
          isOnline: data["isOnline"] === true,
          createdAt: data["createdAt"]
        };
      });
      this.userContacts.set(list);
    }, (err) => {
      console.warn("Search contacts load note:", err);
    });
  }

  shortcutColors = [
    "bg-ios-blue/10 text-ios-blue",
    "bg-ios-purple/10 text-ios-purple",
    "bg-ios-green/10 text-ios-green",
    "bg-ios-orange/10 text-ios-orange",
    "bg-ios-pink/10 text-ios-pink",
    "bg-ios-teal/10 text-ios-teal",
    "bg-ios-indigo/10 text-ios-indigo",
    "bg-ios-red/10 text-ios-red",
  ];

  // Touch swipe handling with strict vertical gesture preservation
  private touchStartX = 0;
  private touchStartY = 0;
  private touchEndX = 0;
  private touchEndY = 0;

  onGlobalTouchStart(event: TouchEvent) {
    if (event.touches.length > 1) return;
    this.touchStartX = event.changedTouches[0].screenX;
    this.touchStartY = event.changedTouches[0].screenY;
  }

  onGlobalTouchEnd(event: TouchEvent) {
    if (event.changedTouches.length === 0) return;
    this.touchEndX = event.changedTouches[0].screenX;
    this.touchEndY = event.changedTouches[0].screenY;
    this.handleHorizontalSwipe();
  }

  private handleHorizontalSwipe() {
    const diffX = this.touchStartX - this.touchEndX;
    const diffY = this.touchStartY - this.touchEndY;
    const swipeThreshold = 75;

    // Strict horizontal check: only trigger category swipe if horizontal motion exceeds vertical motion by 2x
    // This guarantees that vertical up and down scrolling is 100% smooth and never intercepted!
    if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > Math.abs(diffY) * 2) {
      if (this.hasSearched()) {
        const currentIndex = this.searchCategories.findIndex(
          (c) => c.name.toLowerCase() === this.category(),
        );
        if (diffX > 0) {
          // Swipe left -> Next category
          if (currentIndex < this.searchCategories.length - 1) {
            this.onCategorySelect(this.searchCategories[currentIndex + 1].name);
          }
        } else {
          // Swipe right -> Previous category
          if (currentIndex > 0) {
            this.onCategorySelect(this.searchCategories[currentIndex - 1].name);
          }
        }
      } else {
        // Home view: swipe left/right across category chips ('All', 'Search', 'Productivity', 'System')
        const hubList: ('all' | 'search' | 'productivity' | 'system')[] = ['all', 'search', 'productivity', 'system'];
        const currentIndex = hubList.indexOf(this.activeHubCategory());
        if (diffX > 0) {
          // Swipe left -> Next category
          if (currentIndex < hubList.length - 1) {
            this.onHubCategorySelect(hubList[currentIndex + 1]);
          }
        } else {
          // Swipe right -> Previous category
          if (currentIndex > 0) {
            this.onHubCategorySelect(hubList[currentIndex - 1]);
          }
        }
      }
    }
  }

  getShortcutColor(index: number): string {
    return this.shortcutColors[index % this.shortcutColors.length];
  }

  constructor() {
    if (this.isBrowser) {
      window.addEventListener("scroll", () => {
        this.isScrolled.set(window.scrollY > 20);

        // Infinite scroll triggers for Person and Organization
        const scrollPos = window.innerHeight + window.scrollY;
        const threshold = document.body.offsetHeight - 500;
        if (scrollPos >= threshold) {
          const cat = this.category();
          if ((cat === 'person' || cat === 'all') && this.hasMorePersons() && !this.isLoadingMorePersons()) {
            this.loadMorePersons();
          }
          if ((cat === 'organization' || cat === 'all') && this.hasMoreOrgs() && !this.isLoadingMoreOrgs()) {
            this.loadMoreOrgs();
          }
        }
      });
    }

    effect(() => {
      const response = this.searchService.response();
      if (this.isBrowser && response?.chats) {
        response.chats.forEach(chat => {
          const uid = chat.participantId || (chat as any).uid;
          if (uid && !this.fetchedChatAccountUids.has(uid)) {
            this.fetchedChatAccountUids.add(uid);
            getDoc(doc(db, 'accounts', uid)).then(snap => {
              if (snap.exists()) {
                const data = snap.data();
                const accType = (data['accountType'] ?? data['account_type'] ?? '').toString().trim();
                this.chatAccountTypes.update(prev => ({ ...prev, [uid]: accType }));
              } else {
                this.chatAccountTypes.update(prev => ({ ...prev, [uid]: '' }));
              }
            }).catch(() => {
              this.fetchedChatAccountUids.delete(uid);
            });
          }
        });
      }
      if (this.isBrowser && response && this.resultsContainer()) {
        const children =
          this.resultsContainer()?.nativeElement.querySelectorAll(
            ".animate-item",
          );
        if (children && children.length > 0) {
          const prefersReduced =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

          if (prefersReduced) {
            children.forEach((el: any) => {
              el.style.opacity = "1";
              el.style.transform = "none";
            });
          } else {
            animate(
              children,
              {
                opacity: [0, 1],
                y: [10, 0],
                scale: [0.99, 1],
              },
              {
                delay: stagger(0.02),
                duration: 0.25,
                ease: "easeOut",
              },
            );
          }
        }
      }
    });

    effect(() => {
      const currentCat = this.category();
      const isSearched = this.hasSearched();
      if (isSearched && typeof document !== "undefined") {
        setTimeout(() => {
          const el = document.getElementById("cat-btn-" + currentCat);
          if (el) {
            el.scrollIntoView({
              behavior: "smooth",
              inline: "center",
              block: "nearest",
            });
          }
        }, 50);
      }
    });

    effect(() => {
      // Re-evaluate staff access & contacts whenever the current user changes
      this.authService.user();
      if (this.isBrowser) {
        this.checkStaffAccess();
        this.loadUserContacts();
      }
    });
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.detectUserGeoCountry();
      this.checkStaffAccess();
      this.loadUserContacts();
      this.unsubscribeShortcuts = onSnapshot(
        doc(db, "settings", "shortcuts"),
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const map: Record<string, boolean> = {};
            for (const key of Object.keys(data)) {
              if (typeof data[key] === "boolean") {
                map[key] = data[key];
              }
            }
            this.shortcutToggles.set(map);
          } else {
            this.shortcutToggles.set({});
          }
        },
        (err) => {
          console.error("Failed to listen to shortcuts settings:", err);
        }
      );
      setTimeout(() => {
        this.isMounted.set(true);
      }, 0);
    } else {
      this.isMounted.set(true);
    }

    if (
      this.isBrowser &&
      ("webkitSpeechRecognition" in window || "SpeechRecognition" in window)
    ) {
      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = true;

      this.recognition.onstart = () => {
        this.isListening.set(true);
      };

      this.recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join("");
        this.query.set(transcript);
      };

      this.recognition.onerror = (event: any) => {
        console.error("Speech recognition error", event.error);
        this.isListening.set(false);
        if (event.error === "not-allowed") {
          this.notificationService.error(
            "Microphone access denied. Please enable it in your browser settings.",
          );
        } else if (event.error !== "no-speech") {
          this.notificationService.error(
            "Voice search failed. Please try again.",
          );
        }
      };

      this.recognition.onend = () => {
        this.isListening.set(false);
        if (this.query()) {
          this.onSearch();
        }
      };
    }

    this.route.queryParams.subscribe(async (params) => {
      const q = params["q"] || "";
      const c = params["c"] || "all";

      const shouldShowHasSearched = "q" in params || "c" in params;

      if (shouldShowHasSearched) {
        this.query.set(q);
        this.category.set(c);
        this.hasSearched.set(true);
        this.searchService.search(q, c);

        // Fetch jobs, products, and videos reactively from cross-user collections with strict verification validation
        if (c === "all" || c === "jobs") {
          try {
            const rawJobs = await this.searchDataService.getTabData("jobs");
            if (q.trim()) {
              const lowerQ = q.trim().toLowerCase();
              this.jobs.set(rawJobs.filter(item => 
                (item.jobTitle && item.jobTitle.toLowerCase().includes(lowerQ)) ||
                (item.description && item.description.toLowerCase().includes(lowerQ)) ||
                (item.companyInfo && item.companyInfo.toLowerCase().includes(lowerQ))
              ));
            } else {
              this.jobs.set(rawJobs);
            }
          } catch (e) {
            console.error("Error loading jobs for search:", e);
            this.jobs.set([]);
          }
        } else {
          this.jobs.set([]);
        }

        if (c === "all" || c === "products") {
          try {
            const rawProducts = await this.searchDataService.getTabData("products");
            if (q.trim()) {
              const lowerQ = q.trim().toLowerCase();
              this.products.set(rawProducts.filter(item => 
                (item.productName && item.productName.toLowerCase().includes(lowerQ)) ||
                (item.description && item.description.toLowerCase().includes(lowerQ)) ||
                (item.category && item.category.toLowerCase().includes(lowerQ))
              ));
            } else {
              this.products.set(rawProducts);
            }
          } catch (e) {
            console.error("Error loading products for search:", e);
            this.products.set([]);
          }
        } else {
          this.products.set([]);
        }

        if (c === "all" || c === "videos") {
          try {
            const rawVideos = await this.searchDataService.getTabData("videos");
            if (q.trim()) {
              const lowerQ = q.trim().toLowerCase();
              this.videos.set(rawVideos.filter(item => 
                (item.title && item.title.toLowerCase().includes(lowerQ)) ||
                (item.description && item.description.toLowerCase().includes(lowerQ)) ||
                (item.videoCategory && item.videoCategory.toLowerCase().includes(lowerQ))
              ));
            } else {
              this.videos.set(rawVideos);
            }
          } catch (e) {
            console.error("Error loading videos for search:", e);
            this.videos.set([]);
          }
        } else {
          this.videos.set([]);
        }

        // Check if query is a URL
        const isUrl = /^(https?:\/\/|www\.)[^\s]+$/i.test(q.trim());
        if (isUrl) {
          const fullUrl = q.trim().startsWith("www.")
            ? `https://${q.trim()}`
            : q.trim();
          this.webViewUrl.set(fullUrl);
          this.webViewLoading.set(true);

          // Fallback timeout: if it doesn't load in 5 seconds, show the restricted message
          setTimeout(() => {
            if (this.webViewLoading()) {
              this.webViewLoading.set(false);
            }
          }, 5000);
        } else {
          this.webViewUrl.set(null);
        }
      } else {
        this.hasSearched.set(false);
        this.query.set("");
        this.category.set("all");
        this.activeHubCategory.set("all");
        this.searchService.response.set(null);
        this.jobs.set([]);
        this.products.set([]);
        this.videos.set([]);
      }
    });
  }

  ngOnDestroy() {
    if (this.unsubscribeContacts) {
      this.unsubscribeContacts();
      this.unsubscribeContacts = null;
    }
    if (this.unsubscribeShortcuts) {
      this.unsubscribeShortcuts();
      this.unsubscribeShortcuts = null;
    }
  }

  onShortcutClick(shortcut: Shortcut) {
    if (shortcut.path !== "/search") {
      this.router.navigate([shortcut.path]);
      return;
    }

    const newCat = shortcut.queryParams?.["c"] || "all";
    this.category.set(newCat);

    this.router.navigate(["/search"], {
      queryParams: { q: this.query().trim(), c: newCat },
      queryParamsHandling: "merge",
    });
  }

  onSearch() {
    const q = this.query().trim();
    const c = this.category();

    this.router.navigate(["/search"], {
      queryParams: { q, c },
      queryParamsHandling: "merge",
    });
  }

  onFeedback() {
    this.notificationService.success(
      "Thank you for your feedback! We'll use it to improve Kicgle Intelligence.",
    );
  }

  onSourceClick(url: string, event: Event) {
    event.preventDefault();
    this.query.set(url);
    this.webViewUrl.set(url);
    this.webViewLoading.set(true);

    setTimeout(() => {
      if (this.webViewLoading()) {
        this.webViewLoading.set(false);
      }
    }, 5000);

    this.router.navigate(["/search"], {
      queryParams: { q: url, c: this.category() },
      queryParamsHandling: "merge",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  onCategorySelect(cat: string) {
    const newCat = cat.toLowerCase();
    this.category.set(newCat);

    const q = this.query().trim();
    this.router.navigate(["/search"], {
      queryParams: { q, c: newCat },
      queryParamsHandling: "merge",
    });

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        const btn = document.getElementById(`cat-btn-${newCat}`);
        if (btn) {
          btn.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }, 50);
    }
  }

  onReset() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  onVoiceSearch() {
    if (!this.recognition) {
      this.notificationService.info(
        "Voice search is not supported in your browser.",
      );
      return;
    }

    if (this.isListening()) {
      this.recognition.stop();
    } else {
      this.query.set("");
      this.recognition.start();
    }
  }

  onReloadWebView() {
    const url = this.webViewUrl();
    this.webViewUrl.set(null);
    setTimeout(() => {
      this.webViewUrl.set(url);
      this.webViewLoading.set(true);
      setTimeout(() => {
        if (this.webViewLoading()) {
          this.webViewLoading.set(false);
        }
      }, 5000);
    }, 100);
  }

  onWebViewLoad() {
    // Small delay to ensure it's actually rendered
    setTimeout(() => {
      this.webViewLoading.set(false);
    }, 500);
  }
}
